import { CONFIG_FIELD_PREFIX, parseHtmlConfigFields } from '../html-config'
import { allowedRuleTagsFrom } from '../bridge/bridge-protocol'
import { ALLOWED_IMAGE_HOSTS } from '../bridge/frame-policy'
import { decodeHTML } from 'entities'
import { codeRangeAt, lexPage, PageLex, rangeIndexAt, StartTag, startTags } from './code-lexer'

/**
 * code-widget-analysis.ts
 *
 * Reads an app configuration and reports what the Code Widgets in it actually
 * do, so someone configuring an app can answer "what is this page allowed to
 * do, and what does it reach for?" without reading the HTML themselves.
 *
 * Pure: app config JSON in, view-model out. No React, no DOM, no host state —
 * which is what lets the whole thing be unit-tested against fixture configs.
 * The view (see the diagnostics popup in the app configuration top bar) only
 * renders what comes out of here.
 *
 * WHAT THIS IS NOT
 *
 * The scan is lexical: regular expressions over the page source. It reports
 * what a reviewer would see reading the file, which means an obfuscated call —
 * `window['fe' + 'tch']`, a string assembled at runtime, anything arriving
 * through `eval` — passes it unnoticed. A clean report is evidence that a page
 * looks harmless, never proof that it is. SCAN_CAVEAT carries that sentence
 * into the view-model so the view cannot forget to show it.
 *
 * Extending it: add a FindingId and a scanner, or a member to WISTOR_API. The
 * widget-type filter and the bridge surface are both covered by drift tests in
 * code-widget-analysis.test.ts, so a bridge method added without a matching
 * entry here fails the suite rather than quietly going unreported.
 */

export const SCAN_CAVEAT =
  'This is a lexical scan of the page source. Code that hides what it calls — names built ' +
  'at runtime, eval reached indirectly, obfuscation — is not detected. A clean report means the page reads as ' +
  'harmless, not that it is.'

/**
 * The `widget` value a Code Widget node carries. A literal rather than an import
 * of codeWidgetDescription, which would pull React and the lazily-imported
 * component into what is otherwise a dependency-free module; a test asserts the
 * two stay equal.
 */
export const CODE_WIDGET_NAME = 'Code Widget'

// --- view-model ------------------------------------------------------------

export type Severity = 'high' | 'medium' | 'low' | 'info'

const SEVERITY_RANK: Record<Severity, number> = { high: 0, medium: 1, low: 2, info: 3 }

export type Evidence = {
  /** 1-based, into the widget's html. */
  line: number
  snippet: string
}

/**
 * A timer firing faster than this, with a backend call inside, is a load problem
 * rather than a refresh. Declared here because the catalogue below quotes it —
 * the panel should state the threshold rather than say "fast".
 */
const FAST_TIMER_MS = 2000

/**
 * What a detector is handed. Everything is precomputed before any of them run,
 * and every entry is free to look at all of it.
 *
 * `html` is the comment-blanked copy the patterns run over; `raw` is the page as
 * written, needed only to tell an empty widget from a configured one. `page` is
 * `html` with its structure read once: brackets, functions and click-reached code.
 */
export type ScanContext = {
  html: string
  page: Page
  raw: string
  locate: Locator
  egress: EgressTarget[]
  usage: BridgeMemberUsage[]
  configuration: ConfigurationFacts
}

/** What a detector returns when it fires. `title` overrides the entry's, for findings that count things. */
export type Detection = { detail: string; evidence: Evidence[]; title?: string }

/** Evidence for every match of a pattern, one entry per line. */
function evidenceFor(ctx: ScanContext, pattern: RegExp): Evidence[] {
  return dedupeEvidence(matchAll(ctx.html, pattern).map((match) => ctx.locate(match.index)))
}

/**
 * Every code this analysis can report, what makes it fire, and the code that
 * decides so — all in one entry.
 *
 * The catalogue is the source of the `FindingId` type, not a description of it,
 * so a detector emitting an undocumented code does not compile. `detection` is
 * written for whoever reads the panel rather than whoever maintains the regex:
 * it should say what the page has to contain, in plain terms. Nothing forces it
 * to agree with `detect` — that one stays a matter of reading the pair together,
 * which is why they sit adjacent.
 *
 * Detectors are independent. Where one needs what another looks for — the
 * harvesting check needs the secret fields, the fast-timer check needs the timer
 * calls — it recomputes through a shared helper rather than reading another
 * entry's result, so each can be reasoned about and tested alone.
 *
 * Ordered for someone reading what is checked; the report itself sorts by
 * severity.
 */
const CATALOGUE_DEFINITION = [
  {
    id: 'exfiltration-risk',
    title: 'Reads app data and can send it out',
    severity: 'high',
    why:
      'Data in the platform is there under the access rules of the person using the app. A page that can both read ' +
      'that data and send it somewhere outside can put it beyond those rules entirely, and nothing downstream will ' +
      'know it happened. This is the arrangement a leak needs, whether by intent or by a library doing more than ' +
      'anyone expected.',
    detection:
      'Both halves have to be present. The page uses a host member that carries data — data, onData, select, ' +
      'readFile or getViewerConfig — and it also points somewhere outside the platform or somewhere that cannot be ' +
      'read from the source. A fixed link written in the markup does not count — it carries only what is written ' +
      'in it, and is reported as links-out — but an href built in script does. Libraries from the allow-listed CDN ' +
      'paths do not count either, nor do images from the hosts the frame allows them from (ALLOWED_IMAGE_HOSTS).',
    limits:
      'It cannot tell what an outbound call actually carries, so this says the shape is present, not that data is ' +
      'leaving. Equally, a page that assembles its address at runtime is reported as a computed target without ' +
      'anyone being able to say where it points.',
    remedy: { where: 'page', action: 'Remove every request, script, image or link that points outside the platform or at a computed address. Read data through wistor.data or wistor.select() and show it on the page only.' },
    detect: (ctx) => {
      const reading = usedMembers(ctx.usage, (entry) => Boolean(entry.readsHostData))
      const channels = outboundChannels(ctx.egress)
      if (!reading.length || !channels.length) return null

      return {
        detail:
          `The page reads host data (${reading.join(', ')}) and also makes outbound requests. ` +
          'Either alone is ordinary; together they are the shape data would leave the platform in. ' +
          'Check what the outbound calls carry.',
        evidence: dedupeEvidence(channels.map((target) => target.evidence)),
      }
    },
  },
  {
    id: 'credential-harvesting-risk',
    title: 'Asks for a secret and can send it somewhere',
    severity: 'high',
    why:
      'A widget asking for a secret is already odd, since the platform handles signing in. One that can also send ' +
      'what it collects is the working form of a credential-harvesting page, and it sits inside an app people ' +
      'already trust — which is exactly what makes such a page convincing.',
    detection:
      'A password field or a field named after a secret is present, and the page also sends somewhere external, ' +
      'writes to the store (updateQuads, uploadRdf, uploadFile), or forwards through the host (setParam, openUrl).',
    limits:
      'It reads fields, not intent, and a perfectly legitimate field looks identical. It also sees only real input ' +
      'elements: a login form drawn with contenteditable or a canvas is invisible to it.',
    remedy: { where: 'page', action: 'Remove the secret field and every path that stores, sends or forwards what is typed into it. Signing in stays the platform\u2019s job.' },
    detect: (ctx) => {
      const secrets = secretFields(ctx)
      if (!secrets.length) return null

      const channels = outboundChannels(ctx.egress)
      /* A collected secret leaves the page by going out, by being written to the store, or by being handed to another widget. */
      const sinks = CREDENTIAL_SINKS.filter((member) => isUsed(ctx, member))
      if (!channels.length && !sinks.length) return null

      return {
        detail:
          'The page collects something secret and also ' +
          (channels.length ? 'makes outbound requests' : 'writes or forwards it through the host') +
          '. Whether or not that is what it does, this is the arrangement credential theft needs, and it should not ' +
          'be left standing without an explanation.',
        evidence: dedupeEvidence([
          ...secrets.map((tag) => ctx.locate(tag.index)),
          ...channels.map((target) => target.evidence),
          ...sinks.flatMap((member) => evidenceOf(ctx, member)),
        ]),
      }
    },
  },
  {
    id: 'password-input',
    title: 'Asks for a password',
    severity: 'medium',
    why:
      'Signing in is the platform\u2019s job. A widget asking for a password is either duplicating login — which ' +
      'spreads credentials to somewhere that was never meant to hold them — or imitating it. Both deserve an ' +
      'explanation from whoever configured the page.',
    detection: 'An input element carries type="password".',
    limits:
      'Only real password inputs. A field that takes a password while declared as plain text is caught by the ' +
      'secret-named check instead, and one with a meaningless name is caught by neither.',
    remedy: { where: 'page', action: 'Remove the password field. The person is already signed in to the platform, and nothing in a widget needs their password.' },
    detect: (ctx) => {
      const typed = secretFields(ctx).filter((tag) => PASSWORD_TYPE.test(tag.decoded))
      if (!typed.length) return null

      const identity = inputTags(ctx.html).filter((tag) => !PASSWORD_TYPE.test(tag.decoded) && IDENTITY_WORDS.test(tag.decoded))

      return {
        detail:
          'This page renders a password field. Signing in is the platform\u2019s job, so a widget asking for one is ' +
          'either duplicating login or imitating it. ' +
          (identity.length ? 'A username or e-mail field sits alongside it, which is the shape of a login form. ' : '') +
          'Check with whoever configured this what the field is for.',
        evidence: dedupeEvidence(typed.map((tag) => ctx.locate(tag.index))),
      }
    },
  },
  {
    id: 'credential-like-input',
    title: 'Asks for something secret',
    severity: 'medium',
    why:
      'Secrets typed into a widget end up wherever that widget sends them, and a field declared as plain text shows ' +
      'what is typed on screen and is offered to the browser to remember. Citizen numbers and bank details carry ' +
      'the same weight as a password here, and none of them belong in a page configured this way.',
    detection:
      'An input or textarea whose attributes name a secret — password, wachtwoord, pincode, passcode, token, api ' +
      'key, IBAN, BSN, card number, CVV — while not being a password field; or a contenteditable element named ' +
      'after one or labelled "password", "wachtwoord", "pincode" or "passcode" just before it. Dutch and English both.',
    limits:
      'An input or textarea is judged by its attributes, so one labelled "Wachtwoord" in the page text but named ' +
      'field1 is missed. A name is also not proof of purpose: read the field before judging it.',
    remedy: { where: 'page', action: 'Remove the field. If it does not hold a secret, rename it after what it does hold, so its name no longer reads as a password, PIN, token, IBAN or BSN.' },
    detect: (ctx) => {
      const named = secretFields(ctx).filter((tag) => !PASSWORD_TYPE.test(tag.decoded))
      if (!named.length) return null

      return {
        detail:
          'A field here is named after a secret — a password, PIN, token, key, card or citizen number. Names are ' +
          'not proof of purpose, so read the field before judging it, but nothing of that sort should be typed ' +
          'into a widget.',
        evidence: dedupeEvidence(named.map((tag) => ctx.locate(tag.index))),
      }
    },
  },
  {
    id: 'reads-account-data',
    title: 'Queries user accounts',
    severity: 'high',
    why:
      'The user-management graph holds every account of the platform: logins, e-mail addresses and password ' +
      'hashes. Where graph security is off, any query can read it, so a page that asks for it shows it to everyone ' +
      'using the app — and a password hash on screen is one offline attack away from a password.',
    detection:
      'The page source or the widget query names the user-management graph or vocabulary (UserManagement, ' +
      'user-management) or a password predicate (psw).',
    limits:
      'Names only. A query that reaches the same triples through an unbounded pattern — ?s ?p ?o without a ' +
      'graph — reads them just the same and is not caught.',
    remedy: {
      where: 'page',
      action:
        'Remove every query of the user-management graph. Select only the variables the page shows, and never a ' +
        'login, e-mail or password predicate. When the page needs the current user, ask for what it needs to show.',
    },
    detect: (ctx) => {
      const inPage = evidenceFor(ctx, ACCOUNT_DATA)
      const inQuery = ctx.configuration.query != null && new RegExp(ACCOUNT_DATA.source, 'i').test(ctx.configuration.query)
      if (!inPage.length && !inQuery) return null

      return {
        detail:
          (inQuery ? 'The widget query ' : 'The page ') +
          (inQuery && inPage.length ? 'and the page both reach' : 'reaches') +
          ' for the user-management graph. Where graph security is off, that returns logins, e-mail addresses and ' +
          'password hashes, and the page can show them to anyone using the app.',
        evidence: inPage,
      }
    },
  },
  {
    id: 'secret-forwarded',
    title: 'Hands a secret to the rest of the app',
    severity: 'high',
    why:
      'An app variable is read by every widget, trigger and query that names it, and can end up in a shared link. A ' +
      'password hash or token passed there has left the page as surely as one sent over the network, and no ' +
      'outbound call ever shows it.',
    detection:
      'A setParam, setParams or openUrl call whose arguments name a secret: psw, password, wachtwoord, pincode, ' +
      'passcode, secret, token, api key, IBAN or BSN.',
    limits:
      'Names only. A secret copied into an innocently named variable first, and forwarded under that name, is not ' +
      'seen.',
    remedy: {
      where: 'page',
      action:
        'Pass only identifiers and display values through app variables. Never a password, hash, token, key, IBAN ' +
        'or BSN — and do not select those in the first place.',
    },
    detect: (ctx) => {
      const forwarded = matchAll(ctx.html, /\bwistor\s*\.\s*(?:setParams?|openUrl)\s*\(/g).filter((call) => {
        const start = ctx.html.indexOf('(', call.index)
        const end = balancedEnd(ctx.page, start)
        return end > 0 && SECRET_VALUE.test(ctx.html.slice(start, end))
      })
      if (!forwarded.length) return null

      return {
        detail:
          'The page passes something named after a secret into an app variable or a URL. Every widget, trigger and ' +
          'query that reads that variable now holds it, and a shared link can carry it further.',
        evidence: dedupeEvidence(forwarded.map((call) => ctx.locate(call.index))),
      }
    },
  },
  {
    id: 'host-data-stored-as-file',
    title: 'Reads stored data and writes it into a file',
    severity: 'medium',
    why:
      'A graph is read under the permissions of the person reading it, and a query against a graph they may ' +
      'not read is refused. A stored file carries none of that: it is addressed by path and served to anyone ' +
      'signed in. So a page that reads a graph and writes a file moves the contents from the first rule to the ' +
      'second, where the people the graph was closed to can fetch it — and because nothing leaves the ' +
      'platform, every outbound check stays clear while it happens.',
    detection:
      'The page uses a host member that carries data — data, onData, select, readFile or getViewerConfig — ' +
      'and also calls uploadFile.',
    limits:
      'It sees both halves present, not that the data read is the data stored. A page that reads a graph and ' +
      'separately stores a file the person picked looks identical to one that stores the query results.',
    remedy: {
      where: 'page',
      action:
        'Confirm the file carries only what everyone who can reach the repository may see. Where the page needs ' +
        'a pointer rather than a copy, write a triple at the data instead of writing a file.',
    },
    detect: (ctx) => {
      const reading = usedMembers(ctx.usage, (entry) => Boolean(entry.readsHostData))
      if (!reading.length || !isUsed(ctx, 'uploadFile')) return null

      return {
        detail:
          `The page reads stored data (${reading.join(', ')}) and also writes files. A file is not protected by ` +
          'the permissions of the graph its contents came from — anyone signed in who has the path can fetch it.',
        evidence: dedupeEvidence([...reading.flatMap((member) => evidenceOf(ctx, member)), ...evidenceOf(ctx, 'uploadFile')]),
      }
    },
  },
  {
    id: 'frame-escape',
    title: 'Reaches outside its own frame',
    severity: 'high',
    why:
      'The page runs in a sandboxed frame so that it cannot steer the browser tab or read the app around it. Code ' +
      'that reaches for the window above is trying to leave that box. The sandbox should refuse it — which means ' +
      'either the page is quietly broken, or it is not being refused. Neither is a state to leave a configured app in.',
    detection: 'The page addresses top.location, top.document, parent.location or parent.document.',
    limits:
      'Only these spellings. A reference assembled at runtime, or reached through a variable holding the parent ' +
      'window, reads as ordinary code.',
    remedy: { where: 'page', action: 'Remove every reference to top and parent. Open links with wistor.openUrl() and tell the rest of the app about a selection with wistor.setParam().' },
    detect: (ctx) => {
      const evidence = evidenceFor(ctx, /\b(?:top|parent)\s*\.\s*(?:location|document)\b/g)
      if (!evidence.length) return null

      return {
        detail:
          'The page addresses the window above it, to navigate the tab or read the surrounding document. The sandbox ' +
          'should refuse this. Either it is failing silently and the page is broken, or it is not — neither is a state ' +
          'to leave a configured app in.',
        evidence,
      }
    },
  },
  {
    id: 'egress-remote',
    title: 'Reaches external hosts',
    severity: 'medium',
    why:
      'Every external host the page contacts sees a request from the person using the app — their address, their ' +
      'browser, and whatever the page puts in the URL. A host that serves code can also change what it serves at ' +
      'any time, so a script loaded from elsewhere is a standing decision to run someone else\u2019s code inside ' +
      'this app. The list of hosts is the list of parties this app depends on.',
    detection:
      'Anything pointing at an absolute or protocol-relative address: fetch, XMLHttpRequest, sendBeacon, WebSocket, ' +
      'EventSource, dynamic import, and the markup and stylesheet equivalents — script, link, img and iframe ' +
      'sources, form actions, CSS url() and @import — plus src and href assigned in script. Scripts and ' +
      'stylesheets from the allow-listed CDN paths (cdnjs.cloudflare.com/ajax/libs/, cdn.jsdelivr.net/npm/) are listed ' +
      'as libraries instead.',
    limits:
      'Literal addresses only. One built at runtime is reported separately as a computed target, and a request made ' +
      'through a name assembled at runtime is not seen at all.',
    remedy: { where: 'page', action: 'Load libraries only from cdnjs.cloudflare.com/ajax/libs/ or cdn.jsdelivr.net/npm/, and get data only through wistor.select() or wistor.readFile(). A request to any other host is refused by the frame anyway; when the page truly needs an external service, remove the call and say what it was for, because a developer has to provide it as a backend rule.' },
    detect: (ctx) => {
      const remote = ctx.egress.filter(
        (target) => isRemoteNotAllowListed(target) && !isFixedLink(target)
      )
      if (!remote.length) return null

      const hosts = unique(remote.map((target) => target.host ?? 'unknown host'))
      return {
        title: `Reaches ${hosts.length} external ${hosts.length === 1 ? 'host' : 'hosts'}`,
        /* Same verb-phrase shape as the catalogue title it overrides, so both read after "the page". */
        detail: `The page loads from or sends to: ${hosts.join(', ')}. Every one of these sees a request from the person using the app.`,
        evidence: dedupeEvidence(remote.map((target) => target.evidence)),
      }
    },
  },
  {
    id: 'egress-dynamic-target',
    title: 'Makes network calls with a computed target',
    severity: 'medium',
    why:
      'A request whose address is built while the page runs cannot be reviewed by reading the page. It may be ' +
      'ordinary — a path with an identifier in it — or it may be the one call that carries data somewhere it should ' +
      'not go. Either way nobody can tell from the source, so it needs reading by hand rather than trusting.',
    detection:
      'Any of the outbound calls above made with something other than a string literal as its target: a variable, a ' +
      'concatenation, a template.',
    limits:
      'It reports that the target is computed, never what it computes to. A page that builds a harmless URL and one ' +
      'that builds a collector address look exactly alike here.',
    remedy: { where: 'page', action: 'Replace every computed address with a string literal. When the address depends on data, send the person there through wistor.openUrl() on a click, with only the identifier the link needs.' },
    detect: (ctx) => {
      const dynamic = ctx.egress.filter((target) => target.urlClass === 'dynamic')
      if (!dynamic.length) return null

      return {
        detail:
          'The page makes requests whose address is built at runtime, so this report cannot say where they go. ' +
          'These need reading by hand.',
        evidence: dedupeEvidence(dynamic.map((target) => target.evidence)),
      }
    },
  },
  {
    id: 'navigation-away',
    title: 'Navigates the person somewhere else',
    severity: 'medium',
    why:
      'Navigation takes the person out of the app, carrying whatever is in the URL with them, and a page that ' +
      'navigates on load rather than on a click does it without anyone choosing to go. Some of this the sandbox ' +
      'refuses at runtime, which is its own problem: a page written to navigate and silently failing is broken in a ' +
      'way nobody will report.',
    detection:
      'Assigning location or location.href, calling location.assign or replace, opening a window, a meta refresh, ' +
      'or an href assigned in script. Links written in the markup with a fixed address are links-out instead.',
    limits:
      'It cannot tell a navigation that happens on load from one behind a button nobody presses, and it says ' +
      'nothing about whether the sandbox will allow it.',
    remedy: { where: 'page', action: 'Navigate only from a click, through wistor.openUrl(), to a literal address; to move to another app on this site, use wistor.openUrl(path, { inPlace: true }). Remove navigation on load, meta refresh and location assignments.' },
    detect: (ctx) => {
      const navigation = ctx.egress.filter(
        (target) => NAVIGATION_KINDS.includes(target.kind) && !STAYS_ON_PLATFORM.includes(target.urlClass) && !isFixedLink(target)
      )
      if (!navigation.length) return null

      return {
        detail:
          'The page sets a location, opens a window or builds a link in script. Some of this the sandbox refuses at runtime, ' +
          'so treat a finding here as "the page tries to", not "the page does" — a navigation that silently fails is ' +
          'its own kind of broken, and one that succeeds takes the person off the app with whatever is in the URL.',
        evidence: dedupeEvidence(navigation.map((target) => target.evidence)),
      }
    },
  },
  {
    id: 'links-out',
    title: 'Links to other sites',
    severity: 'low',
    why:
      'A link takes the person to another site when they click it. Written in the markup with a fixed address, it ' +
      'carries only what is written in it, so it cannot take data with it — but where it leads is still worth a ' +
      'look, since the person trusts this app to send them somewhere sensible.',
    detection: 'An <a> or <area> in the markup whose href is a fixed address outside the platform.',
    limits:
      'The address is read as written; what the site at the other end does is not. A link whose href is built in ' +
      'script is navigation-away, and counts as a way data can leave.',
    remedy: { where: 'page', action: 'Keep only links to sites the people using the app should go to, and write each address out in full.' },
    detect: (ctx) => {
      const links = ctx.egress.filter(isFixedLink)
      if (!links.length) return null

      const hosts = unique(links.map((target) => target.host ?? 'unknown host'))
      return {
        detail: `The page links to: ${hosts.join(', ')}. Confirm each is somewhere the people using the app should go.`,
        evidence: dedupeEvidence(links.map((target) => target.evidence)),
      }
    },
  },
  {
    id: 'replaces-app-on-load',
    title: 'Replaces the app without a click',
    severity: 'low',
    why:
      'wistor.openUrl(path, { inPlace: true }) swaps the whole app for another one on this site. From a button that ' +
      'is how a person moves between apps. Run when the page loads, it moves everyone who opens this app somewhere ' +
      'else before they see it, the people configuring it included, and two apps doing it to each other bounce for ever.',
    detection: 'A wistor.openUrl call with inPlace: true that runs without a click: on load, from a timer, or from data arriving.',
    limits:
      'Only inPlace: true written out counts; options held in a variable are read as a new tab. The host keeps the ' +
      'address on this site, but a page on this site that redirects onwards is not followed.',
    remedy: { where: 'page', action: 'Call wistor.openUrl(path, { inPlace: true }) only from a click, so the person chooses to leave this app.' },
    detect: (ctx) => {
      const unprompted = ctx.egress.filter(
        (target) => target.urlClass === 'same-origin' && !isUserTriggered(ctx.page, target.index)
      )
      if (!unprompted.length) return null

      return {
        detail: 'The page replaces this app with another one without anyone clicking. Confirm the people using the app should be sent on.',
        evidence: dedupeEvidence(unprompted.map((target) => target.evidence)),
      }
    },
  },
  {
    id: 'backend-interaction',
    title: 'Reads or writes stored data',
    severity: 'medium',
    why:
      'This is what most Code Widgets are for, so it is not a fault — but it is the thing worth knowing first. What ' +
      'the page can see and change is whatever the person using the app is allowed to see and change, and a page ' +
      'that writes can write anything they could. Stated so that a reviewer confirms the reach is one this page ' +
      'should have, rather than inferring it from the absence of anything else.',
    detection:
      'The page uses a host member that reaches the backend — select, updateQuads, readFile, uploadRdf, ' +
      'uploadFile, callRule — ' +
      'or one through which query results arrive, data and onData.',
    limits:
      'It says which doors are used, not what goes through them. The scope of a query or the contents of a write ' +
      'are not examined.',
    remedy: { where: 'page', action: 'Confirm the page reads and writes only the data it needs: name the variables in each SELECT and the graph of each write.' },
    detect: (ctx) => {
      const reaching = ctx.usage.filter((entry) => entry.used && entry.reachesBackend)
      if (!reaching.length) return null

      return {
        detail:
          `The page reaches the backend through ${reaching.map((entry) => `wistor.${entry.member}`).join(', ')}. ` +
          'What it can see and change there is whatever the person using the app is allowed to.',
        evidence: dedupeEvidence(reaching.flatMap((entry) => entry.evidence)),
      }
    },
  },
  {
    id: 'deletes-without-user-action',
    title: 'Deletes data without anyone clicking',
    severity: 'high',
    why:
      'A delete that runs on load, on new query results or on a timer runs for everyone who opens the app, as ' +
      'often as the page decides — and the person whose permissions it runs under never chose to delete anything. ' +
      'One wrong filter then empties a graph for every user, and nothing in the app asks first.',
    detection:
      'An updateQuads call with deletes is reached outside a click or submit handler — directly, or through a ' +
      'function that no click or submit handler calls.',
    limits:
      'Handlers are followed through named functions only: a delete reached through a function kept in an ' +
      'object or passed around as a value reads as unguarded. Legacy writes, which replace every stored value of a ' +
      'property, are not counted as deletes.',
    remedy: {
      where: 'page',
      action:
        'Run every delete only from a click on a visible button, after a confirmation that names what is deleted ' +
        'and how many triples. Delete only triples the page read and showed, never the result of a broad query.',
    },
    detect: (ctx) => {
      const unguarded = deletingWrites(ctx.page).filter((index) => !isUserTriggered(ctx.page, index))
      if (!unguarded.length) return null

      return {
        detail:
          'The page deletes triples from code that runs without a click — on load, on new data or on a timer. It ' +
          'runs for everyone who opens the app, under their permissions, and nobody is asked first.',
        evidence: dedupeEvidence(unguarded.map((index) => ctx.locate(index))),
      }
    },
  },
  {
    id: 'deletes-on-click',
    title: 'Deletes data when clicked',
    severity: 'medium',
    why:
      'Deleting from a button is ordinary — it is how a page removes a value it shows. It is listed because a delete ' +
      'cannot be undone from the page, and whether the button deletes only what the person sees is decided by code ' +
      'nobody reads.',
    detection: 'An updateQuads call with deletes, reached only from click or submit handlers.',
    limits: 'It sees that a click starts the delete, not what the delete matches or whether the page asks first.',
    remedy: {
      where: 'page',
      action:
        'Confirm before deleting, naming what is deleted and how many triples, and delete only the triples the page ' +
        'read and showed.',
    },
    detect: (ctx) => {
      const writes = deletingWrites(ctx.page)
      if (!writes.length || !writes.every((index) => isUserTriggered(ctx.page, index))) return null

      return {
        detail: 'The page deletes triples from a click. Confirm it asks first and deletes only what it shows.',
        evidence: dedupeEvidence(writes.map((index) => ctx.locate(index))),
      }
    },
  },
  {
    id: 'file-stored-without-user-action',
    title: 'Stores a file without anyone clicking',
    severity: 'medium',
    why:
      'An upload that runs on load, on new query results or on a timer runs for everyone who opens the app, ' +
      'under their permissions, and the person it runs as never chose to store anything. Each one leaves a file ' +
      'and the triples describing it, so what the page writes grows with how often the app is opened rather ' +
      'than with what anyone asked for.',
    detection:
      'A wistor.uploadFile call is reached outside a click or submit handler — directly, or through a function ' +
      'that no click or submit handler calls.',
    limits:
      'Handlers are followed through named functions only: an upload reached through a function kept in an ' +
      'object or passed around as a value reads as unguarded, which makes a legitimate save button look like ' +
      'this. The repeated cases — a loop, a timer, a subscription — are reported as backend load as well.',
    remedy: {
      where: 'page',
      action:
        'Store a file only from a click on a visible button, so the person using the app decides that something ' +
        'is kept. Where the upload has to follow data arriving, keep it behind a control that says so.',
    },
    detect: (ctx) => {
      const unguarded = memberCalls(ctx.page, 'uploadFile').filter((index) => !isUserTriggered(ctx.page, index))
      if (!unguarded.length) return null

      return {
        detail:
          'The page stores a file from code that runs without a click — on load, on new data or on a timer. It ' +
          'runs for everyone who opens the app, under their permissions, and nobody is asked first.',
        evidence: dedupeEvidence(unguarded.map((index) => ctx.locate(index))),
      }
    },
  },
  {
    id: 'unbounded-triple-query',
    title: 'Queries every triple',
    severity: 'medium',
    why:
      'A pattern with subject, predicate and object all unbound returns everything the person may read — where graph ' +
      'security is off, that includes the user-management graph with its password hashes. Few pages need that, and ' +
      'one that shows the result shows all of it.',
    detection:
      'A group in the page source or widget query holding nothing but one fully unbound triple pattern, such as ' +
      '{ ?s ?p ?o } or GRAPH ?g { ?s ?p ?o }.',
    limits:
      'Only the bare shape. A pattern bound by a VALUES or FILTER in the same group is not reported, and neither is ' +
      'one bound from outside the group — which reads the same whether or not it is narrowed.',
    remedy: {
      where: 'page',
      action: 'Bind the subject or the predicate in every triple pattern, and name the graph the page reads from.',
    },
    detect: (ctx) => {
      const inPage = evidenceFor(ctx, UNBOUNDED_TRIPLE)
      const inQuery = ctx.configuration.query != null && new RegExp(UNBOUNDED_TRIPLE.source).test(ctx.configuration.query)
      if (!inPage.length && !inQuery) return null

      return {
        detail:
          (inQuery ? 'The widget query' : 'The page') +
          ' asks for every triple it can reach. Where graph security is off, that includes logins and password hashes.',
        evidence: inPage,
      }
    },
  },
  {
    id: 'backend-call-in-timer-high-frequency',
    title: 'Calls the backend on a fast timer',
    severity: 'high',
    why:
      'At this rate a single open app puts continuous load on the store, and the cost multiplies by everyone who ' +
      'has the app open. It is rarely deliberate — usually a refresh interval set in milliseconds where seconds ' +
      'were meant.',
    detection:
      `A setInterval that fires more often than every ${FAST_TIMER_MS}ms, or has no delay at all, and whose own ` +
      'callback calls the backend — inline, or through the named functions it calls.',
    limits:
      'Only a literal delay is read. A rate computed at runtime, or a setTimeout poll that re-arms itself, reports ' +
      'as an ordinary timer call however fast it turns out to be.',
    remedy: { where: 'page', action: 'Remove the timer. Query once on load and again only on a click or when wistor.onData() reports new results.' },
    detect: (ctx) => {
      const fast = fastIntervals(ctx.page)
      if (!fast.length) return null

      return {
        detail:
          `A timer fires every ${fast.map((hit) => `${hit.delay}ms`).join(', ')} and calls ` +
          `${unique(fast.flatMap((hit) => hit.members)).join(', ')} each time. ` +
          'At this rate one open app is enough to put continuous load on the store.',
        evidence: dedupeEvidence(fast.map((hit) => ctx.locate(hit.index))),
      }
    },
  },
  {
    id: 'backend-call-recursive',
    title: 'Calls the backend and starts itself again',
    severity: 'high',
    why:
      'A timer at least states a rate. Recursion states nothing: the next request goes out as soon as the last one ' +
      'answers, for as long as the app is open, and a slow backend makes it worse rather than better because the ' +
      'calls queue instead of skipping. One page left open can hold a share of the store to itself.',
    detection:
      'A named function — declared, or assigned to a const, let or var — whose body both calls the backend and ' +
      'reaches itself again, either by calling its own name or by handing it to something that will call it, such ' +
      'as a then or a timer. The report names the lines where each of those happens, so the loop can be read rather ' +
      'than taken on trust.',
    limits:
      'Names only, and only its own name. Mutual recursion between two functions, a self-reference through a ' +
      'reassigned variable, and an arrow with an expression body rather than a block are all missed — as is a loop ' +
      'closed by looking the function up in an object.',
    remedy: { where: 'page', action: 'Stop the function from starting itself again after a backend call. Query once, and again only on a click or when wistor.onData() reports new results.' },
    detect: (ctx) => {
      const hits = recursiveBackendFunctions(ctx.page)
      if (!hits.length) return null

      /* Each loop spelled out: declared here, calls the backend there, reaches itself again there. */
      const described = hits.map((hit) => {
        const at = (index: number) => ctx.locate(index).line
        return (
          `${hit.name} is declared on line ${at(hit.declaredAt)}, calls the backend on ` +
          `${hit.backendCalls.map(at).join(', ')}, and reaches itself again on ${hit.selfRefs.map(at).join(', ')}`
        )
      })

      return {
        title: hits.length === 1 ? `${hits[0].name} calls the backend and starts itself again` : undefined,
        detail:
          `${described.join('. ')}. Nothing bounds that: it continues for as long as the app is open, and a slow ` +
          'answer queues the next request rather than skipping it.',
        evidence: dedupeEvidence(
          hits.flatMap((hit) =>
            [hit.declaredAt, ...hit.backendCalls, ...hit.selfRefs].map((index) => ctx.locate(index))
          )
        ),
      }
    },
  },
  {
    id: 'backend-call-in-timer',
    title: 'Calls the backend on a timer',
    severity: 'medium',
    why:
      'Every tick is a request, per open app, whether or not anyone is looking at the page. A refresh that seems ' +
      'cheap on one screen becomes constant load once the app is in daily use by a department.',
    detection: 'A backend call appears inside a setInterval, setTimeout or requestAnimationFrame callback.',
    limits:
      'The call has to be inside the callback. A timer that re-arms a function which calls the backend elsewhere is ' +
      'reported as recursion instead. Nothing here judges the rate — that is the fast-timer check.',
    remedy: { where: 'page', action: 'Remove the timer around the backend call. Query once on load and again only on a click or when wistor.onData() reports new results.' },
    detect: (ctx) => {
      const hits = timerCalls(ctx.page)
      if (!hits.length) return null

      return {
        detail:
          `A timer repeatedly calls ${unique(hits.map((hit) => hit.member)).join(', ')}. ` +
          'Every tick is a request per open app, which multiplies by the number of people using it.',
        evidence: dedupeEvidence(hits.map((hit) => ctx.locate(hit.index))),
      }
    },
  },
  {
    id: 'backend-call-in-loop',
    title: 'Calls the backend inside a loop',
    severity: 'medium',
    why:
      'The number of requests follows the size of the data rather than anything the page controls, so this scales ' +
      'with the store instead of with the page. It usually works fine on the handful of rows it was written ' +
      'against and falls over on real data, by which time the loop is far from whoever is watching.',
    detection:
      'A backend call appears inside a for or while loop, with or without braces, or inside a forEach or map ' +
      'callback — unless it sits in a click handler the callback attaches.',
    limits:
      'reduce, a recursive walk over the data, and loops through a library helper are not seen here, though recursion ' +
      'may be caught by its own check.',
    remedy: { where: 'page', action: 'Make one backend call for the whole set instead of one per item: a single SELECT with VALUES, or a single updateQuads with every triple.' },
    detect: (ctx) => {
      /* A forEach that attaches click handlers holds backend calls too, but they run per click, not per row. */
      const handlers = userActionRegions(ctx.page)
      const perRow = callsInsideCalls(ctx.page, /\.\s*(?:forEach|map)\s*\(/g).filter(
        (hit) => !handlers.some(([from, to]) => hit.index >= from && hit.index < to)
      )
      const hits = [...callsInsideBlocks(ctx.page, /\bfor\s*\(/g), ...callsInsideBlocks(ctx.page, /\bwhile\s*\(/g), ...perRow]
      if (!hits.length) return null

      return {
        detail:
          `A loop body calls ${unique(hits.map((hit) => hit.member)).join(', ')}. ` +
          'The number of requests follows the data size, so this scales with the store rather than with the page.',
        evidence: dedupeEvidence(hits.map((hit) => ctx.locate(hit.index))),
      }
    },
  },
  {
    id: 'backend-call-in-subscription',
    title: 'Calls the backend from an update handler',
    severity: 'medium',
    why:
      'If the call changes what the handler is listening to, the two feed each other and the page loops as fast as ' +
      'the backend can answer. Nothing in the page says so, which is what makes it hard to find once it is running: ' +
      'the loop lives in the relationship between two pieces of code, not in either of them.',
    detection: 'A backend call appears inside an onData, onConfig or onConfigFields handler.',
    limits:
      'It cannot tell a feedback loop from a handler that legitimately fetches something unrelated; that depends on ' +
      'what the call touches, which is not examined.',
    remedy: { where: 'page', action: 'Keep update handlers free of backend calls. Derive what the page needs from the data the handler receives, or query on a click.' },
    detect: (ctx) => {
      const hits = callsInsideCalls(ctx.page, /\bwistor\s*\.\s*on(?:Data|Config|ConfigFields)\s*\(/g)
      if (!hits.length) return null

      return {
        detail:
          `An update handler calls ${unique(hits.map((hit) => hit.member)).join(', ')}. ` +
          'If that call changes what the handler listens to, the two feed each other and the page loops.',
        evidence: dedupeEvidence(hits.map((hit) => ctx.locate(hit.index))),
      }
    },
  },
  {
    id: 'clipboard-read',
    title: 'Reads the clipboard',
    severity: 'high',
    why:
      'The clipboard holds whatever the person last copied, from anywhere on their machine — regularly a password ' +
      'coming out of a password manager, often content from an application that has nothing to do with this app. A ' +
      'page that reads it takes all of that without anyone pasting anything.',
    detection: 'The page calls navigator.clipboard.read or readText, or execCommand("paste").',
    limits:
      'The browser asks permission for clipboard reads in most contexts, so a fired check is not the same as a ' +
      'successful read — but a page written to try is worth knowing about either way.',
    remedy: { where: 'page', action: 'Remove the clipboard read. Let the person paste into a field themselves.' },
    detect: (ctx) =>
      whenMatched(
        ctx,
        /navigator\s*\.\s*clipboard\s*\.\s*read(?:Text)?\s*\(|execCommand\s*\(\s*['"]paste['"]/g,
        'The page reads whatever the person last copied. That is regularly a password out of a password manager, ' +
          'or content from another application entirely, and the page gets it without anyone pasting anything.'
      ),
  },
  {
    id: 'clipboard-write',
    title: 'Writes to the clipboard',
    severity: 'info',
    why:
      'Almost always a copy button, which is why this asks for confirmation rather than crossing anything out. It is ' +
      'listed because it is real clipboard use and a reader should not have to wonder whether it was checked — and ' +
      'because a page that replaces the clipboard with something other than what was asked for looks the same here.',
    detection: 'The page calls navigator.clipboard.write or writeText, or execCommand("copy").',
    limits: 'It cannot see what is written, only that something is.',
    remedy: { where: 'page', action: 'Write to the clipboard only in the click handler of a visible, labelled copy button, and only the value that button names.' },
    detect: (ctx) =>
      whenMatched(
        ctx,
        /navigator\s*\.\s*clipboard\s*\.\s*write(?:Text)?\s*\(|execCommand\s*\(\s*['"]copy['"]/g,
        'The page puts content on the clipboard, usually a copy button. Ordinary, listed for completeness.'
      ),
  },
  {
    id: 'cookie-access',
    title: 'Touches cookies',
    severity: 'medium',
    why:
      'Cookies are how sessions are carried. A Code Widget has no ordinary reason to touch them, and a page that ' +
      'reads them may be reading something that identifies the person to the platform rather than anything of its ' +
      'own.',
    detection: 'The page reads or writes document.cookie.',
    limits:
      'Reads and writes look the same to this check, and cookies marked HttpOnly are invisible to the page anyway ' +
      'so the attempt may achieve nothing.',
    remedy: { where: 'page', action: 'Remove every use of document.cookie. The frame has no cookies of the app, and state belongs in the page or in app variables.' },
    detect: (ctx) =>
      whenMatched(
        ctx,
        /document\s*\.\s*cookie/g,
        'The page reads or writes document.cookie. A Code Widget has no ordinary reason to, and cookies are how ' +
          'sessions are carried.'
      ),
  },
  {
    id: 'browser-storage',
    title: 'Stores data in the browser',
    severity: 'low',
    why:
      'Browser storage sits outside the platform: nothing backs it up, nothing expires it, and none of the app\u2019s ' +
      'access rules apply to it. It also outlives the session on a shared machine, so whatever a page keeps there ' +
      'is still there for the next person.',
    detection: 'The page mentions localStorage, sessionStorage or indexedDB.',
    limits:
      'A mention, not a use — naming it in a comment or a feature check counts. It also cannot see what is stored, ' +
      'which is the part that decides whether it matters.',
    remedy: { where: 'page', action: 'Keep state in memory, or in app variables through wistor.setParam(). Remove localStorage, sessionStorage and IndexedDB use.' },
    detect: (ctx) =>
      whenMatched(
        ctx,
        /\b(?:localStorage|sessionStorage|indexedDB)\b/g,
        'The page keeps data in browser storage, which outlives the session and sits outside the platform, ' +
          'where nothing backs it up, expires it or subjects it to the app\u2019s access rules.'
      ),
  },
  {
    id: 'dynamic-code',
    title: 'Runs code built at runtime',
    severity: 'high',
    why:
      'Code assembled from strings while the page runs is not in the source anyone reads, so every other check here ' +
      'is answering for a program the page does not run. Generated pages have no reason to do it: the whole page ' +
      'is already code, written in the open.',
    detection:
      'In script code: eval in any form, the Function constructor (also through x.constructor.constructor), a ' +
      'string handed to setTimeout or setInterval, any bracket lookup on window, self, globalThis, document, top, ' +
      'parent or frames, a script element created in any case, document.write, srcdoc, createContextualFragment, ' +
      'import() of a data: or blob: URL, a script loaded from a data: or blob: URL, a string that holds a tag with ' +
      'an on… handler, and a character reference other than a quote, &, < or > in a handler attribute.',
    limits:
      'A string held in a variable and passed to a timer (setTimeout(code)) reads like setTimeout(render). An alias ' +
      'of the window held in a variable, or code reached through a library, reads as ordinary code. A character ' +
      'reference in a handler attribute (wistor&#46;updateQuads) is reported here rather than decoded, since ' +
      'decoding in place would move every offset.',
    remedy: {
      where: 'page',
      action:
        'Write the code out in the page itself. Replace eval and new Function with ordinary functions, and pass ' +
        'setTimeout and setInterval a function rather than a string.',
    },
    detect: (ctx) => {
      const hits = dynamicCode(ctx.page)
      if (!hits.length) return null

      return {
        detail:
          'The page builds or looks up code while it runs — from strings, computed names, markup it writes in or a ' +
          'script element it creates. What that code does is not in the source, so nothing else in this report can ' +
          'answer for it.',
        evidence: dedupeEvidence(hits.map((index) => ctx.locate(index))),
      }
    },
  },
  {
    id: 'bridge-indirect',
    title: 'Holds the bridge under another name',
    severity: 'high',
    why:
      'Every check on deletes, writes, forwarding, loops and timers recognises a bridge call by its spelling, ' +
      'wistor.member. A page that takes the bridge apart or renames it — destructured, assigned to another ' +
      'variable, bound, or looked up by a computed key — makes those calls under names no check reads, so the ' +
      'rest of this report answers for a page that does nothing. Generated pages have no reason to do it.',
    detection:
      'wistor is destructured or assigned to another name, spread, handed to bind, call or apply, or looked up ' +
      'with a computed key (wistor[name]). wistor?.x, wistor["x"] and wistor.x?.() read as wistor.x and are not ' +
      'counted here.',
    limits:
      'Passing the bridge as an argument (init(wistor)), or reaching it through window under another name, is not ' +
      'seen; following those needs a JavaScript parser (limit 1).',
    remedy: {
      where: 'page',
      action: 'Call every bridge function as wistor.member(…), written out at the call, with no alias or helper in between.',
    },
    detect: (ctx) =>
      whenMatched(
        ctx,
        BRIDGE_INDIRECT,
        'The page holds the bridge under another name, so its calls through that name are invisible to every ' +
          'other check here. What they delete, write or send is not in this report.'
      ),
  },
  {
    id: 'reviewer-addressed-text',
    title: 'Addresses whoever reviews it',
    severity: 'high',
    why:
      'A page is reviewed by people and, increasingly, by language models. Text written to the reviewer — "this ' +
      'page is pre-approved", "ignore previous instructions" — has no purpose for the person using the app. Its ' +
      'only effect is on the review, which makes it the one kind of comment that is never innocent.',
    detection:
      'Comments or text address a reviewer, review agent or AI, claim prior approval, or tell the reader to ignore ' +
      'or override instructions.',
    limits:
      'Phrasing varies without end; this catches the common forms in English and Dutch. Instructions hidden in data ' +
      'the page loads at runtime are not in the source at all.',
    remedy: {
      where: 'page',
      action: 'Remove every comment or text that addresses a reviewer, a review agent or an AI, or claims the page was approved.',
    },
    /* Over the raw page, not the comment-blanked copy: a comment is exactly where such text is put. */
    detect: (ctx) => {
      const evidence = dedupeEvidence(matchAll(ctx.raw, REVIEWER_ADDRESS).map((match) => ctx.locate(match.index)))
      if (!evidence.length) return null

      return {
        detail:
          'The page contains text written to whoever reviews it, not to the person using it. It has no effect ' +
          'on the app, only on the review — treat the page as hostile until that text is explained.',
        evidence,
      }
    },
  },
  {
    id: 'embedded-media',
    title: 'Carries its own images',
    severity: 'low',
    why:
      'Images stored inside the page are shown to everyone using the app, and nobody reviews them the way app data ' +
      'is reviewed. What they show is decided by whoever generated the page — which is how unwanted content reaches ' +
      'a screen without passing anyone.',
    detection: 'The page contains data: URIs for images, audio or video.',
    limits:
      'It sees that media is embedded, never what it shows, and text written into the page is not judged at all. ' +
      'Remote images are not listed here: the frame refuses them.',
    remedy: {
      where: 'page',
      action:
        'Show images from files stored in the platform through wistor.readFile(), or keep each inline image and say ' +
        'in one line what it shows so the configurator can confirm it.',
    },
    detect: (ctx) => {
      const embedded = matchAll(ctx.html, EMBEDDED_MEDIA)
      if (!embedded.length) return null

      return {
        title: `Carries ${embedded.length} embedded ${embedded.length === 1 ? 'image or clip' : 'images or clips'}`,
        detail:
          'The page carries media inside its own source. Confirm that each is meant to be shown to everyone using ' +
          'the app — no check here can say what it depicts.',
        evidence: dedupeEvidence(embedded.map((match) => ctx.locate(match.index))),
      }
    },
  },
  {
    id: 'mutation-logs-without-graph',
    title: 'Has edit-logs enabled without a graph to write them in',
    severity: 'medium',
    why:
      'Every write from this widget throws at runtime, so the page does not half-work — it does not work. And the ' +
      'setting reads as though changes are being recorded when nothing is being recorded at all, which is the worse ' +
      'half: someone may be relying on an audit trail that does not exist.',
    detection: 'Changelog logging is switched on for the widget while no log graph is configured.',
    limits: 'Configuration only. Whether the configured graph is the right one, or writable, is not checked.',
    remedy: { where: 'widget-settings', action: 'Set the edit-log graph in the widget settings, or switch edit-logs off.' },
    detect: (ctx) => {
      const { mutationLogs } = ctx.configuration
      if (!mutationLogs.enabled || mutationLogs.graph) return null

      return {
        detail:
          'Changelog logging is on but no log graph is configured, which makes every write from this widget throw. ' +
          'Set the log graph, or turn the logging off.',
        evidence: [],
      }
    },
  },
  {
    id: 'rule-call-without-allow-list',
    title: 'Tries to start rules it may not start',
    severity: 'medium',
    why:
      'Every such call is refused at runtime, so one of two things is true and they need different fixes: the page ' +
      'is broken and the rule it depends on never runs, or the allow-list was emptied deliberately and the page was ' +
      'never updated to match. Nothing on screen tells the person using the app which.',
    detection: 'The page calls callRule while the widget lists no allowed rule tags.',
    limits: 'It cannot tell which of the two happened; that is a question for whoever configured the widget.',
    remedy: { where: 'widget-settings', action: 'Add the rule tags the page starts to the widget setting allowedRuleTags, or remove the callRule calls from the page.' },
    detect: (ctx) => {
      if (!isUsed(ctx, 'callRule') || ctx.configuration.allowedRuleTags.length) return null

      return {
        detail:
          'The page calls callRule and the widget allows no rule tags, so every call is refused at runtime. Either ' +
          'the page is broken and the rule never runs, or the allow-list was emptied without the page being changed. ' +
          'Both need someone to decide which was meant.',
        evidence: evidenceOf(ctx, 'callRule'),
      }
    },
  },
  {
    id: 'quads-update-without-target-graph',
    title: 'Writes triples with no target graph configured',
    severity: 'low',
    why:
      'With no target graph configured, every triple has to name its own graph or pass one per update, and any that ' +
      'does not fails at runtime. Writes that work in testing and fail on one code path are the kind that get ' +
      'noticed long after the data should have been there.',
    detection: 'The page calls updateQuads while the widget has no targetGraph configured.',
    limits: 'Whether the page names a graph itself is not checked, so this can fire on a page that is perfectly fine.',
    remedy: { where: 'widget-settings', action: 'Set targetGraph in the widget settings to the graph this page writes to.' },
    detect: (ctx) => {
      if (!isUsed(ctx, 'updateQuads') || ctx.configuration.targetGraph) return null

      return {
        detail:
          'The page writes triples and the widget has no targetGraph, so every triple has to name its own graph or ' +
          'pass one per update. Any that does not fails at runtime.',
        evidence: evidenceOf(ctx, 'updateQuads'),
      }
    },
  },
  {
    id: 'rdf-upload-without-target-graph',
    title: 'Uploads RDF with no target graph configured',
    severity: 'low',
    why:
      'Same as writing triples, with more at stake: an upload that names no graph itself fails at runtime, and bulk ' +
      'loads are usually the step someone runs once and assumes worked.',
    detection: 'The page calls uploadRdf while the widget has no targetGraph configured.',
    limits: 'Whether the upload names a graph of its own is not checked.',
    remedy: { where: 'widget-settings', action: 'Set targetGraph in the widget settings to the graph this page uploads to.' },
    detect: (ctx) => {
      if (!isUsed(ctx, 'uploadRdf') || ctx.configuration.targetGraph) return null

      return {
        detail:
          'The page calls uploadRdf and the widget has no targetGraph, so an upload that names no graph itself fails at runtime.',
        evidence: evidenceOf(ctx, 'uploadRdf'),
      }
    },
  },
  {
    id: 'file-upload-without-target-graph',
    title: 'Stores files with no target graph configured',
    severity: 'low',
    why:
      'An upload naming no graph falls back to one shared platform graph, which nobody configuring this widget ' +
      'chose. Where graph security is on, writing that graph needs a permission granted for it by name, so the ' +
      'usual outcome is that every upload is refused. And the graph is part of each file\u2019s identity rather ' +
      'than a label on it, so setting targetGraph afterwards leaves what is already stored where it landed.',
    detection: 'The page calls uploadFile while the widget has no targetGraph configured.',
    limits: 'Whether the page passes a graph of its own per upload is not checked.',
    remedy: { where: 'widget-settings', action: 'Set targetGraph in the widget settings to the graph this page should describe its files in.' },
    detect: (ctx) => {
      if (!isUsed(ctx, 'uploadFile') || ctx.configuration.targetGraph) return null

      return {
        detail:
          'The page stores files and the widget has no targetGraph, so the triples describing them go to the ' +
          'shared default graph — which, with graph security on, this page is usually not allowed to write.',
        evidence: evidenceOf(ctx, 'uploadFile'),
      }
    },
  },
  /*
   * Rules split three ways, and the three mean quite different things:
   * the page starts rules it is allowed to, it tries to start rules it is not
   * allowed to, or it is allowed rules it never starts. Only the first is a
   * live privilege; reporting them as one surface hid which of the three a
   * reviewer was looking at.
   */
  {
    id: 'rules-called',
    title: 'Starts backend rules',
    severity: 'medium',
    why:
      'A rule runs on the server with whatever reach it has, and this page can set it going. Whatever those rules ' +
      'do to the store, this page can cause — so the allow-list is a grant of power and worth confirming it is one ' +
      'this page should hold.',
    detection: 'The page calls callRule and the widget lists one or more allowed rule tags.',
    limits: 'What the rules themselves do is outside this scan; only the fact that they can be started is reported.',
    remedy: { where: 'page', action: 'Confirm every rule the page starts is one it should start, and start each one only from a click.' },
    detect: (ctx) => {
      const tags = ctx.configuration.allowedRuleTags
      if (!isUsed(ctx, 'callRule') || !tags.length) return null

      return {
        title: `Starts backend ${tags.length === 1 ? 'rule' : 'rules'}: ${tags.join(', ')}`,
        detail:
          `This page starts backend rules, and is allowed to start: ${tags.join(', ')}. ` +
          'Whatever those rules do to the store, this page can trigger.',
        evidence: evidenceOf(ctx, 'callRule'),
      }
    },
  },
  {
    id: 'rules-whitelisted-unused',
    title: 'Is allowed rules it never starts',
    severity: 'low',
    why:
      'The grant stands whether or not anything uses it. A later edit to the page — by someone who never saw the ' +
      'allow-list being set — inherits the right to start those rules without anyone deciding to give it. Narrowing ' +
      'the list to what the page actually starts keeps the two in step.',
    detection: 'The widget lists one or more allowed rule tags while the page never calls callRule.',
    limits:
      'A call made through a name assembled at runtime is not seen, so a page may be using the grant in a way this ' +
      'reads as unused.',
    remedy: { where: 'widget-settings', action: 'Remove the unused tags from allowedRuleTags in the widget settings.' },
    detect: (ctx) => {
      const tags = ctx.configuration.allowedRuleTags
      if (!tags.length || isUsed(ctx, 'callRule')) return null

      return {
        detail:
          `The widget allows ${tags.join(', ')}, and the page starts none of them. The grant stands whether or not ` +
          'it is used, so a later edit to the page inherits it without anyone deciding to give it. Narrow the list ' +
          'to what the page actually starts, or empty it.',
        evidence: [],
      }
    },
  },
  {
    id: 'query-without-data-use',
    title: 'Runs a query whose results it never reads',
    severity: 'info',
    why:
      'The query runs on every render and the result goes nowhere. That is load on the store bought for nothing, ' +
      'and it usually means the page was rewritten and the configuration was not — so the query is also a ' +
      'misleading record of what this widget needs.',
    detection: 'The widget has a query configured while the page never reads data and never subscribes with onData.',
    limits:
      'The page may read the result under a name this scan does not recognise, in which case the query is doing its ' +
      'job and this is a false alarm.',
    remedy: { where: 'widget-settings', action: 'Remove the widget query in the widget settings, or read its results in the page through wistor.data or wistor.onData().' },
    detect: (ctx) => {
      const unread = ctx.configuration.hasQuery && !isUsed(ctx, 'data') && !isUsed(ctx, 'onData')
      if (!unread || !ctx.raw.trim()) return null

      return {
        detail:
          'The widget runs a SPARQL query on every render and the page never reads the result. ' +
          'Either the page reads it under a name this scan does not recognise, or the query is dead weight.',
        evidence: [],
      }
    },
  },
  {
    id: 'html-missing',
    title: 'Has no page configured',
    severity: 'info',
    why:
      'The widget renders a placeholder and does nothing. Harmless, but it occupies a place in the layout and reads ' +
      'to anyone reviewing the app as a page that has not been written yet — or as one whose content was lost.',
    detection: 'The widget has no HTML configured at all.',
    remedy: { where: 'widget-settings', action: 'Paste the page into the widget\u2019s html setting, or remove the widget.' },
    detect: (ctx) =>
      ctx.raw.trim()
        ? null
        : {
            detail: 'This Code Widget has no HTML, so it renders the placeholder and does nothing.',
            evidence: [],
          },
  },
] as const satisfies readonly CatalogueEntry[]

/**
 * The catalogue as consumers read it: by its contract, not by the literal type of each entry. The
 * literal type exists only to derive FindingId; read through it, an optional field such as `limits`
 * is missing from the entries that leave it out rather than `undefined`, and does not type-check.
 */
export const FINDING_CATALOGUE: readonly (CatalogueEntry & { id: FindingId })[] = CATALOGUE_DEFINITION

type CatalogueEntry = {
  id: string
  title: string
  severity: Severity
  /** Why this matters and what it prevents — the reason anyone should care that it fired. */
  why: string
  /** What the scan looks for, in terms of what the page has to contain. */
  detection: string
  /** What this check cannot see, so a clean result is read for what it is worth. */
  limits?: string
  /** What to change so the check stops firing, phrased for the agent that wrote the page. Feeds the fix prompt. */
  remedy: Remedy
  detect: (ctx: ScanContext) => Detection | null
}

/**
 * Where a finding gets fixed. `page` is the HTML, which the configurator's coding agent rewrites;
 * `widget-settings` is the widget's own options, which the configurator changes by hand.
 */
export type Remedy = { where: 'page' | 'widget-settings'; action: string }

/* --- shared by several detectors, so each stays independent of the others --- */

/**
 * Ways data could leave: anything pointing outside the platform, or anywhere
 * that cannot be read from the source.
 *
 * Links are counted like the rest. They need a click, which is a reason to look
 * at them rather than a reason to ignore them — a link with data on its query
 * string carries it the moment someone follows it, and "the person clicked"
 * describes how the data left, not whether it did.
 */
function outboundChannels(egress: EgressTarget[]): EgressTarget[] {
  return egress.filter(
    (target) =>
      (isRemoteNotAllowListed(target) && !isFixedLink(target)) ||
      target.urlClass === 'dynamic'
  )
}

/**
 * A link in the markup with a fixed address. It needs a click, and it carries only what is written in it, so
 * it is a place the person may go (links-out, amber) rather than a way data leaves. An href assigned in script
 * is `dynamic-href`, and stays a way out.
 */
const STAYS_ON_PLATFORM: UrlClass[] = ['relative', 'same-origin']

function isFixedLink(target: EgressTarget): boolean {
  return target.kind === 'anchor-href' && target.urlClass === 'remote'
}

/**
 * Where a library may come from without counting as a way out: a CDN and the part of it that serves
 * published packages. The path matters — cdn.jsdelivr.net/gh/<user>/<repo>/ serves any public GitHub
 * repository, so the whole host would let anyone's code in. Even /npm/ is anyone's package; listing
 * packages rather than paths is the next step if "allow-listed" is to mean "trusted code".
 *
 * The widget frame's CSP (ALLOWED_SCRIPT_HOSTS in bridge/frame-policy.ts) should carry the
 * same paths, since a CSP source can: https://cdn.jsdelivr.net/npm/.
 */
export const ALLOWED_LIBRARY_SOURCES = [
  { host: 'cdnjs.cloudflare.com', pathPrefix: '/ajax/libs/' },
  { host: 'cdn.jsdelivr.net', pathPrefix: '/npm/' },
] as const

/**
 * A script or stylesheet from an allow-listed CDN is how nearly every generated page gets its chart or
 * map library. Counting it as a way out would turn the reference examples red, and a signal that is red
 * for everyone is read by no one. It stays visible in the library inventory.
 */
function isAllowListedImage(target: EgressTarget): boolean {
  if (target.kind !== 'img-src' || target.urlClass !== 'remote' || !target.url) return false
  const location = parsedUrl(target.url)
  return location?.protocol === 'https:' && ALLOWED_IMAGE_HOSTS.some((host) => location.hostname === host)
}

/**
 * Remote, and not one of the two ways out the frame itself allows: a library from the allow-listed CDN
 * paths, or an image from an allow-listed host. Fixed links are left to the caller, since only some
 * checks count them.
 */
function isRemoteNotAllowListed(target: EgressTarget): boolean {
  return target.urlClass === 'remote' && !isAllowListedLibrary(target) && !isAllowListedImage(target)
}

function isAllowListedLibrary(target: EgressTarget): boolean {
  if (!LIBRARY_KINDS.includes(target.kind) || target.urlClass !== 'remote' || !target.url) return false
  const location = parsedUrl(target.url)
  return ALLOWED_LIBRARY_SOURCES.some(
    (source) => location?.hostname === source.host && location.pathname.startsWith(source.pathPrefix)
  )
}

/**
 * The URL as the browser resolves it — dot segments, `%2e%2e` and backslashes included — so
 * `/npm/../gh/x` is read as the /gh/ path it requests. Null for anything that is not an absolute address.
 */
function parsedUrl(url: string): URL | null {
  try {
    return new URL(url.trim().replace(/^[/\\]{2}/, 'https://'))
  } catch {
    return null
  }
}

/** Input and textarea tags that are password fields or are named after a secret. */
function secretFields(ctx: ScanContext): Tag[] {
  const inputs = inputTags(ctx.html).filter((tag) => PASSWORD_TYPE.test(tag.decoded) || CREDENTIAL_WORDS.test(tag.decoded))
  /* A contenteditable element has no type or name to read, so the label written just before it decides. */
  const editables = startTags(ctx.html, () => true).filter(
    (tag) =>
      EDITABLE_ATTRIBUTE.test(tag.decoded) &&
      (CREDENTIAL_WORDS.test(tag.decoded) || SECRET_LABEL.test(textBefore(ctx.html, tag.index, LABEL_REACH_CHARS)))
  )
  return [...inputs, ...editables].sort((a, b) => a.index - b.index)
}

type Tag = StartTag

function inputTags(html: string): Tag[] {
  return startTags(html, (name) => name === 'input' || name === 'textarea')
}

/** The visible text in the `chars` before `index`, markup stripped — where a field's label sits. */
function textBefore(html: string, index: number, chars: number): string {
  return html.slice(Math.max(0, index - chars), index).replace(/<[^<>]*>/g, ' ')
}

/**
 * Backend calls reached from a timer, recomputed rather than shared so the two
 * timer checks stay independent.
 *
 * `setTimeout` counts: a handler that re-arms itself after each response is the
 * ordinary way to write a poll, and arguably worse than an interval, since it
 * survives a slow backend by queueing rather than by skipping a tick.
 */
function timerCalls(code: Code): BackendHit[] {
  return [
    ...callsInsideCalls(code, /\bsetInterval\s*\(/g),
    ...callsInsideCalls(code, /\bsetTimeout\s*\(/g),
    ...callsInsideCalls(code, /\brequestAnimationFrame\s*\(/g),
  ]
}

/**
 * Functions that call the backend and then call themselves.
 *
 * `function load() { wistor.select(q).then(load) }` never stops, and no timer
 * appears anywhere in it — the recursion is the loop. Caught by finding a named
 * function whose body holds both a backend call and a mention of its own name.
 *
 * Names only, so an anonymous self-reference through a variable that is
 * reassigned, or mutual recursion between two functions, goes unseen.
 */
function recursiveBackendFunctions(page: Page): RecursiveFunction[] {
  const text = page.text
  return [...page.functions.values()].flatMap(({ name, declaredAt, nameAt, body: [bodyStart, bodyEnd] }) => {
    const backendCalls = matchAll(text.slice(bodyStart, bodyEnd), BACKEND_CALL_PATTERN).map((hit) => bodyStart + hit.index)
    if (!backendCalls.length) return []

    /*
     * A mention is not a call. `name(` is one, and `then(name)` is one because the reference is handed to
     * something that will call it. The same name as a property, in a string, handed to removeEventListener,
     * or as a parameter of a function inside the body (`.then(render => draw(render))`) is not.
     */
    const shadowed = shadowingFunctions(page, name, [bodyStart, bodyEnd])
    const selfRefs = (page.references.get(name) ?? []).filter((at) => {
      if (at < bodyStart || at >= bodyEnd || at === nameAt) return false
      if (shadowed.some(([from, to]) => at >= from && at < to)) return false
      const after = skipSpace(text, at + name.length)
      if (/^(?:\.\s*(?:call|apply)\s*)?\(/.test(text.slice(after, after + 16))) return true
      const before = text.slice(Math.max(bodyStart, at - 80), at)
      if (/\bremoveEventListener\s*\([^()]*,\s*$/.test(before)) return false
      return /[(,]\s*$/.test(before) && /^[,)]/.test(text[after] ?? '')
    })
    if (!selfRefs.length) return []

    return [{ name, declaredAt, backendCalls, selfRefs }]
  })
}

/** Functions inside `region` that take a parameter called `name`: inside them, `name` is that parameter. */
function shadowingFunctions(page: Page, name: string, [from, to]: [number, number]): [number, number][] {
  const slice = page.text.slice(from, to)
  const lists = matchAll(slice, PARAMETER_LIST)
    .filter((match) => (match[1] ?? match[2]).split(',').some((parameter) => parameter.split('=')[0].trim() === name))
    .map((match) => match.index)
  const single = matchAll(slice, new RegExp(`(?<![\\w$.])${escapeRegExp(name)}\\s*=>`, 'g')).map((match) => match.index)
  return [...lists, ...single].flatMap((at) => {
    const body = functionAt(page, from + at)
    return body ? [[from + at, body[1]] as [number, number]] : []
  })
}

type RecursiveFunction = { name: string; declaredAt: number; backendCalls: number[]; selfRefs: number[] }

function isUsed(ctx: ScanContext, member: WistorMember): boolean {
  return ctx.usage.some((entry) => entry.member === member && entry.used)
}

function evidenceOf(ctx: ScanContext, member: WistorMember): Evidence[] {
  return ctx.usage.find((entry) => entry.member === member)?.evidence ?? []
}

/** The common shape: a pattern, and one sentence if it matched anywhere. */
function whenMatched(ctx: ScanContext, pattern: RegExp, detail: string): Detection | null {
  const evidence = evidenceFor(ctx, pattern)
  return evidence.length ? { detail, evidence } : null
}

/**
 * Run every detector. Order in the report comes from severity, not from the
 * catalogue — the catalogue is ordered for someone reading what is checked.
 */
function runCatalogue(ctx: ScanContext): Finding[] {
  return FINDING_CATALOGUE.flatMap((entry) => {
    const found = entry.detect(ctx)
    if (!found) return []

    return [
      {
        id: entry.id,
        severity: entry.severity,
        title: found.title ?? entry.title,
        detail: found.detail,
        evidence: found.evidence,
        remedy: entry.remedy,
      },
    ]
  }).sort(bySeverityThenId)
}

export type FindingId = (typeof CATALOGUE_DEFINITION)[number]['id']

export type Finding = {
  id: FindingId
  severity: Severity
  title: string
  detail: string
  evidence: Evidence[]
  remedy: Remedy
}

export type EgressKind =
  | 'fetch'
  | 'xhr'
  | 'beacon'
  | 'websocket'
  | 'eventsource'
  | 'dynamic-import'
  | 'script-src'
  | 'link-href'
  | 'img-src'
  | 'media-src'
  | 'object-src'
  | 'iframe-src'
  | 'form-action'
  | 'css-url'
  | 'css-import'
  | 'open-url'
  | 'location-assign'
  | 'window-open'
  | 'anchor-href'
  | 'meta-refresh'
  | 'dynamic-src'
  | 'dynamic-href'

/**
 * `remote` is decided from the URL alone, because the analysis is pure and has
 * no host to compare against: an absolute or protocol-relative URL is remote, a
 * relative one is not. `dynamic` means the target was not a literal, so nothing
 * can be said about where it points — reviewed by hand or not at all.
 * `same-origin` is `wistor.openUrl(url, { inPlace: true })`: the host opens it
 * only on its own origin, whatever the URL says, so it stays on the platform.
 */
export type UrlClass = 'remote' | 'relative' | 'same-origin' | 'inline' | 'dynamic'

export type EgressTarget = {
  kind: EgressKind
  /** As written in the source, placeholders and all. Null when the target is not a literal. */
  url: string | null
  host: string | null
  urlClass: UrlClass
  /** Offset into the widget's html, for detectors that ask whether a click starts it. */
  index: number
  evidence: Evidence
}

export type WistorMemberKind = 'value' | 'subscribe' | 'request' | 'fire-and-forget'

export type WistorMember =
  | 'data'
  | 'config'
  | 'configFields'
  | 'onData'
  | 'onConfig'
  | 'onConfigFields'
  | 'select'
  | 'updateQuads'
  | 'readFile'
  | 'uploadRdf'
  | 'uploadFile'
  | 'callRule'
  | 'getViewerConfig'
  | 'setParam'
  | 'setParams'
  | 'toast'
  | 'openUrl'

export type BridgeMemberUsage = {
  member: WistorMember
  kind: WistorMemberKind
  description: string
  /** Stored data is involved: the page calls the backend, or backend results arrive through it. */
  reachesBackend: boolean
  used: boolean
  occurrences: number
  evidence: Evidence[]
}

export type RiskId =
  | 'exfiltration'
  | 'credentials'
  | 'account-data'
  | 'data-destruction'
  | 'review-evasion'
  | 'backend-interaction'
  | 'backend-overload'
  | 'file-store'
  | 'external-network'
  | 'navigation'
  | 'local-data'
  | 'embedded-content'

/**
 * `review` is the honest middle: behaviour that is not wrong, but that a person
 * should confirm was meant. A copy button is the case it was built for — real
 * clipboard use, ordinary in itself, and silently green next to a visible
 * clipboard observation reads as a contradiction.
 */
export type RiskStatus = 'clear' | 'review' | 'flagged'

export type RiskVerdict = {
  id: RiskId
  label: string
  status: RiskStatus
  /** Reads as the consequence when flagged, as what to check when it wants review. */
  note: string
  /** The findings that decided it, so the verdict can be traced rather than trusted. */
  findingIds: FindingId[]
  /** Whether this answer colours the Lock&Publish check. See RiskCategory.publishCheck. */
  countsForPublish: boolean
}

type RiskCategory = {
  id: RiskId
  label: string
  consequence: string
  findings: FindingId[]
  /** Findings that earn a look rather than a cross. */
  review?: FindingId[]
  reviewNote?: string
  /**
   * `ignored` keeps the risk out of the Lock&Publish colour while it stays in the report. For a risk
   * nearly every widget raises, counting it would make the colour the same for everyone.
   */
  publishCheck?: 'ignored'
}

const CLEAR_NOTE = 'not detected'

/**
 * The named risks, in the order they matter, each answered for every widget
 * whether or not anything was found. Reporting only what was detected leaves a
 * reader unable to tell "no exfiltration" from "exfiltration was never checked",
 * and those are not the same statement.
 *
 * Benign findings are deliberately absent. Writing to the clipboard is ordinary
 * and appears in the observations; putting it here would turn a copy button into
 * a red cross and teach people to ignore the crosses.
 */
const RISK_CATEGORIES: RiskCategory[] = [
  {
    id: 'exfiltration',
    label: 'Data exfiltration',
    consequence: 'The page reads app data and can send it out of the platform.',
    findings: ['exfiltration-risk', 'secret-forwarded'],
  },
  {
    id: 'credentials',
    label: 'Credential harvesting',
    consequence: 'The page asks the person for a password or another secret.',
    findings: ['credential-harvesting-risk', 'password-input', 'credential-like-input'],
  },
  {
    id: 'account-data',
    label: 'Account data exposure',
    consequence: 'The page queries user accounts — logins, e-mail addresses, password hashes — and can show them.',
    findings: ['reads-account-data'],
    review: ['unbounded-triple-query'],
    reviewNote: 'The page queries every triple it can reach. Confirm it needs to, and bind what it reads.',
  },
  {
    id: 'data-destruction',
    label: 'Data destruction',
    consequence: 'The page deletes stored data without anyone clicking.',
    findings: ['deletes-without-user-action'],
    review: ['deletes-on-click'],
    reviewNote: 'The page deletes data from a button. Confirm it asks first and deletes only what it shows.',
  },
  {
    id: 'review-evasion',
    label: 'Hidden from review',
    consequence:
      'The page runs code built at runtime, renames the bridge, or addresses its reviewer, so reading it does not say what it does.',
    findings: ['dynamic-code', 'bridge-indirect', 'reviewer-addressed-text'],
  },
  {
    /*
     * Never flagged, only ever cleared or amber. Touching stored data is what
     * most Code Widgets are for — the point is that a reviewer sees it stated
     * rather than inferring it from the absence of anything else.
     */
    id: 'backend-interaction',
    label: 'Backend interaction',
    consequence: 'The page reads or writes data through the host.',
    findings: [],
    review: ['backend-interaction', 'rules-called'],
    publishCheck: 'ignored',
    reviewNote:
      'The page reads or writes stored data through the host, or may start backend rules. Expected of most widgets — ' +
      'worth confirming the data it reaches, and the rules it can trigger, are ones this page should have.',
  },
  {
    id: 'backend-overload',
    label: 'Backend overload',
    consequence: 'The page can put repeated or unbounded load on the store.',
    findings: [
      'backend-call-recursive',
      'backend-call-in-timer-high-frequency',
      'backend-call-in-timer',
      'backend-call-in-loop',
      'backend-call-in-subscription',
    ],
  },
  {
    /*
     * Separate from exfiltration, which means leaving the platform. This one
     * stays inside it: a stored file is addressed by path and served to anyone
     * signed in, so it is not held to the permissions of the graph its contents
     * came from. Storing a file is also what a good half of these pages are for,
     * which is why reading-and-storing asks for confirmation rather than being
     * crossed out, and only an upload nobody asked for is flagged.
     */
    id: 'file-store',
    label: 'Files written to the repository',
    consequence: 'The page writes files that anyone signed in can download, without being asked.',
    findings: ['file-stored-without-user-action'],
    review: ['host-data-stored-as-file'],
    reviewNote:
      'The page reads stored data and also writes files. A file is not protected by the permissions of the ' +
      'graph its contents came from — confirm it carries only what its readers may see.',
  },
  {
    id: 'external-network',
    label: 'External network calls',
    consequence: 'The page contacts hosts outside the platform.',
    findings: ['egress-remote', 'egress-dynamic-target'],
  },
  {
    id: 'navigation',
    label: 'Navigation and frame escape',
    consequence: 'The page moves the person elsewhere, or reaches outside its frame.',
    findings: ['frame-escape', 'navigation-away'],
    review: ['links-out', 'replaces-app-on-load'],
    reviewNote: 'The page links to other sites, or moves to another app on this site by itself. Confirm each is somewhere the people using the app should go.',
  },
  {
    id: 'local-data',
    label: 'Clipboard and browser storage',
    consequence: 'The page reads the clipboard, or keeps data outside the platform.',
    findings: ['clipboard-read', 'browser-storage', 'cookie-access'],
    review: ['clipboard-write'],
    reviewNote: 'The page writes to the clipboard. Ordinary for a copy button — worth confirming that is what it is.',
  },
  {
    id: 'embedded-content',
    label: 'Embedded content',
    consequence: 'The page shows media stored in its own source.',
    findings: [],
    review: ['embedded-media'],
    reviewNote: 'The page carries its own images or clips. Confirm each is meant to be shown to everyone using the app.',
  },
]

/**
 * Which risk each finding answers, for both directions of the report: a
 * catalogue entry can name its category, and a category can name the checks it
 * watches. Built from the categories, so neither view can drift from the other.
 *
 * Not every finding belongs to one. The configuration mistakes — a missing
 * target graph, an empty rule allow-list — are worth reporting without being
 * risks in the sense the verdict list means.
 */
export const RISKS_BY_FINDING: Partial<Record<FindingId, { id: RiskId; label: string }[]>> =
  RISK_CATEGORIES.reduce<Partial<Record<FindingId, { id: RiskId; label: string }[]>>>((byFinding, category) => {
    for (const finding of [...category.findings, ...(category.review ?? [])]) {
      /* A finding can answer more than one risk, and is listed under each rather than claimed by the first. */
      byFinding[finding] = [...(byFinding[finding] ?? []), { id: category.id, label: category.label }]
    }
    return byFinding
  }, {})

/** Every check a category watches, fired or not. */
export function findingsWatchedBy(risk: RiskId): FindingId[] {
  const category = RISK_CATEGORIES.find((candidate) => candidate.id === risk)
  return category ? [...category.findings, ...(category.review ?? [])] : []
}

/**
 * The findings a widget shows as "confirm this": those behind a category it left in review. Taken from the
 * verdicts, so a finding is never coloured differently from the risk row it caused — the same check sits in
 * a flagged verdict (unbounded-triple-query beside reads-account-data) as a violation.
 */
export function reviewFindingIds(risks: RiskVerdict[]): Set<FindingId> {
  return new Set(risks.filter((risk) => risk.status === 'review').flatMap((risk) => risk.findingIds))
}

/**
 * Findings that can put a category into "confirm this" rather than crossing it out — what a check can do,
 * for the catalogue. Whether it did, for a widget or the app, is reviewFindingIds and AppAnalysis.
 * Derived from the categories, so the catalogue and the report cannot disagree.
 */
export const REVIEW_FINDING_IDS: FindingId[] = unique(
  RISK_CATEGORIES.flatMap((category) => category.review ?? [])
)

/** Answer every category for one widget: what fired, and so whether it is clean, worth a look, or flagged. */
function riskVerdicts(findings: Finding[]): RiskVerdict[] {
  const present = new Set(findings.map((finding) => finding.id))

  return RISK_CATEGORIES.map((category) => {
    const flagging = category.findings.filter((id) => present.has(id))
    const reviewing = (category.review ?? []).filter((id) => present.has(id))
    return verdict(category, flagging, reviewing)
  })
}

/** Roll the per-widget answers up: the app takes the worst answer any widget gave. */
function rollUpRisks(widgets: CodeWidgetAnalysis[]): RiskVerdict[] {
  return RISK_CATEGORIES.map((category) => {
    const found = unique(
      widgets.flatMap((widget) => widget.risks.find((risk) => risk.id === category.id)?.findingIds ?? [])
    )
    return verdict(
      category,
      category.findings.filter((id) => found.includes(id)),
      (category.review ?? []).filter((id) => found.includes(id))
    )
  })
}

function verdict(category: RiskCategory, flagging: FindingId[], reviewing: FindingId[]): RiskVerdict {
  const status: RiskStatus = flagging.length ? 'flagged' : reviewing.length ? 'review' : 'clear'

  return {
    id: category.id,
    label: category.label,
    status,
    note:
      status === 'flagged'
        ? category.consequence
        : status === 'review'
        ? category.reviewNote ?? 'Worth confirming this was intended.'
        : CLEAR_NOTE,
    findingIds: [...flagging, ...reviewing],
    countsForPublish: category.publishCheck !== 'ignored',
  }
}

const STATUS_RANK: Record<RiskStatus, number> = { clear: 0, review: 1, flagged: 2 }

/**
 * The Lock&Publish colour: the worst answer among the risks that count for it. Works for one widget's
 * risks and for the app's rolled-up ones alike.
 */
export function publishCheckStatus(risks: RiskVerdict[]): RiskStatus {
  return risks
    .filter((risk) => risk.countsForPublish)
    .reduce<RiskStatus>((worst, risk) => (STATUS_RANK[risk.status] > STATUS_RANK[worst] ? risk.status : worst), 'clear')
}

/** The findings behind a widget's publish colour — what the configurator has to fix to change it. */
export function publishCheckFindings(widget: CodeWidgetAnalysis): Finding[] {
  const deciding = new Set(
    widget.risks.filter((risk) => risk.countsForPublish && risk.status !== 'clear').flatMap((risk) => risk.findingIds)
  )
  return widget.findings.filter((finding) => deciding.has(finding.id))
}

export type Library = {
  /** Package name where a CDN URL gives one, otherwise the script's filename. */
  name: string
  version: string | null
  url: string
  evidence: Evidence
}

export type ConfigurationFacts = {
  hasQuery: boolean
  /** The query in full, so a reviewer can read what the page is fed without opening the config. */
  query: string | null
  allowedRuleTags: string[]
  targetGraph: string | null
  minHeight: string | null
  mutationLogs: { enabled: boolean; graph: string | null; customStatements: string | null }
  /**
   * Declared by the page, with the value each placement stores for it — one
   * entry per path, in the same order, since the same page configured twice is
   * one report but need not be one setting.
   */
  declaredConfigFields: { name: string; type: string; label: string; values: (string | null)[] }[]
}

export type CodeWidgetAnalysis = {
  /**
   * Every place this widget sits, e.g. "mainArea > areas[1] > areas[0]". More
   * than one means the identical widget is configured in several places — an
   * embedded app used twice, say — and it is analyzed once for all of them.
   */
  paths: string[]
  widgetId: string | null
  title: string | null
  htmlLines: number
  /**
   * The page itself. Carried in the report so a reviewer can read the line an
   * evidence entry names without going back to the configuration to find it.
   */
  html: string
  /** Every named risk, answered yes or no. Always all of them, in priority order. */
  risks: RiskVerdict[]
  findings: Finding[]
  bridgeUsage: BridgeMemberUsage[]
  egress: EgressTarget[]
  libraries: Library[]
  /**
   * Bridge members that reach the backend. These are network calls too — they
   * leave the browser — but they go through the host to the configured store
   * rather than anywhere the page chooses, so they are listed apart from egress.
   */
  backendCalls: WistorMember[]
  configuration: ConfigurationFacts
}

export type AppAnalysis = {
  appTitle: string | null
  widgets: CodeWidgetAnalysis[]
  /** The same named risks across the whole app: flagged when any widget flags them. */
  risks: RiskVerdict[]
  /** Every widget the walk passed, Code Widget or not — so an empty report can say whether it read anything at all. */
  widgetsScanned: number
  /**
   * How many of those were inside an embedded app, and how many Component
   * widgets they came through. In real apps most of the tree is embedded, often
   * the same app several times over, and "2 of 276" hides that entirely.
   */
  widgetsInComponents: number
  componentWidgets: number
  /** The distinct widget types found, which is what tells you a name has drifted rather than a widget being absent. */
  widgetTypes: string[]
  /** Worst severity across every widget, for a badge on the button that opens the view. */
  highestSeverity: Severity | null
  /**
   * Findings shown as "confirm this" in the app summary: a finding is one only when it put a category into
   * review in every widget where it fired. Fired anywhere as part of a flagged verdict, it is a violation.
   */
  reviewFindingIds: FindingId[]
  /** The summary in checks: of every check in the catalogue, how many passed, need confirming, or flag. */
  findingCounts: { checks: number; passed: number; toConfirm: number; violations: number }
  caveat: string
}

// --- the wistor API surface ------------------------------------------------

type WistorApiEntry = {
  member: WistorMember
  kind: WistorMemberKind
  description: string
  /** Extra spellings that count as using the member: legacy globals and the DOM events bootstrap dispatches. */
  aliases?: string[]
  /** Reaches data the page would not otherwise hold — one half of the exfiltration pair. */
  readsHostData?: boolean
  /** Costs the backend a request, so calling it in a timer or a loop is a load problem. */
  callsBackend?: boolean
}

/**
 * Members that carry backend query results into the page without the page
 * asking for them — the host runs the widget query and hands the results over.
 * Highlighted alongside the members that do call out, since to a reviewer the
 * question is the same: is stored data involved here?
 */
const QUERY_RESULT_MEMBERS: WistorMember[] = ['data', 'onData']

/** Either the page calls the backend, or the backend's data arrives through it. */
function reachesBackend(entry: WistorApiEntry): boolean {
  return Boolean(entry.callsBackend) || QUERY_RESULT_MEMBERS.includes(entry.member)
}

export const WISTOR_API: readonly WistorApiEntry[] = [
  { member: 'data', kind: 'value', description: 'SPARQL results of the widget query', aliases: ['WIDGET_DATA', 'widget:data'], readsHostData: true },
  { member: 'config', kind: 'value', description: "The widget's config JSON", aliases: ['WIDGET_CONFIG', 'widget:config'] },
  { member: 'configFields', kind: 'value', description: 'Values of the page-declared config fields', aliases: ['WIDGET_CONFIG_FIELDS', 'widget:configfields'] },
  { member: 'onData', kind: 'subscribe', description: 'Subscribe to query-result updates', readsHostData: true },
  { member: 'onConfig', kind: 'subscribe', description: 'Subscribe to config updates' },
  { member: 'onConfigFields', kind: 'subscribe', description: 'Subscribe to config-field updates' },
  { member: 'select', kind: 'request', description: 'Run an ad-hoc SPARQL SELECT', readsHostData: true, callsBackend: true },
  { member: 'updateQuads', kind: 'request', description: 'Write triples to the store', callsBackend: true },
  { member: 'readFile', kind: 'request', description: 'Read a file from the repository', readsHostData: true, callsBackend: true },
  { member: 'uploadRdf', kind: 'request', description: 'Bulk-load an RDF document', callsBackend: true },
  { member: 'uploadFile', kind: 'request', description: 'Store a file and write its metadata triples', callsBackend: true },
  { member: 'callRule', kind: 'request', description: 'Start a backend rule', callsBackend: true },
  { member: 'getViewerConfig', kind: 'request', description: 'Read the app configuration', readsHostData: true },
  { member: 'setParam', kind: 'fire-and-forget', description: 'Set one host app-variable' },
  { member: 'setParams', kind: 'fire-and-forget', description: 'Set several host app-variables' },
  { member: 'toast', kind: 'fire-and-forget', description: 'Show a host notification' },
  { member: 'openUrl', kind: 'fire-and-forget', description: 'Open a URL in a new tab, or in place of the app on this site' },
]

// --- entry point -----------------------------------------------------------

/**
 * Analyze every Code Widget in an app config. Takes `unknown` because the
 * config is freeform JSON from the store: anything unrecognisable is skipped
 * rather than thrown on, so one malformed node never costs the whole report.
 */
export function analyzeViewerConfig(config: unknown): AppAnalysis {
  return analyzeViewerConfigReporting(config, () => {})
}

/**
 * analyzeViewerConfig, telling `onWidget` which widget it is about to read before each one — so a scan that
 * is stopped part-way can name the page it was stuck on.
 */
export function analyzeViewerConfigReporting(config: unknown, onWidget: (label: string) => void): AppAnalysis {
  const root = asRecord(config)
  const nodes = collectWidgetNodes(root?.mainArea, '')
  const widgets = groupByIdentity(nodes.filter((found) => found.node.widget === CODE_WIDGET_NAME)).map((widget) => {
    onWidget(asString(widget.nodes[0].titleMenubar) ?? asString(widget.nodes[0].id) ?? widget.paths[0])
    return analyzeCodeWidget(widget)
  })

  return {
    appTitle: asString(root?.title),
    widgets,
    risks: rollUpRisks(widgets),
    widgetsScanned: nodes.length,
    widgetsInComponents: nodes.filter((found) => found.embedded).length,
    componentWidgets: nodes.filter((found) => found.node.widget === COMPONENT_WIDGET_NAME).length,
    widgetTypes: unique(nodes.map((found) => asString(found.node.widget) ?? 'unnamed')).sort(),
    highestSeverity: highestSeverity(widgets),
    ...appFindingSummary(widgets),
    caveat: SCAN_CAVEAT,
  }
}

function appFindingSummary(widgets: CodeWidgetAnalysis[]): Pick<AppAnalysis, 'reviewFindingIds' | 'findingCounts'> {
  const fired = unique(widgets.flatMap((widget) => widget.findings.map((finding) => finding.id)))
  const confirmOnly = fired.filter((id) =>
    widgets.every((widget) => !widget.findings.some((finding) => finding.id === id) || reviewFindingIds(widget.risks).has(id))
  )
  return {
    reviewFindingIds: confirmOnly,
    findingCounts: {
      checks: FINDING_CATALOGUE.length,
      passed: FINDING_CATALOGUE.length - fired.length,
      toConfirm: confirmOnly.length,
      violations: fired.length - confirmOnly.length,
    },
  }
}

type FoundWidget = { node: Record<string, unknown>; path: string; embedded: boolean }

/**
 * How far into a widget the walk may go, per widget type.
 *
 * A stored config holds more than the app renders. A SplitLayout draws one area
 * per entry in `areaSizes` and ignores the rest; a Tab and a BBSideMenu draw one
 * per entry in `tabs`; a Component renders the app under `component`, never its
 * own `areas`. Configuration outside those bounds is left over from earlier
 * edits — it is in the JSON, nothing puts it on screen, and reporting on it
 * describes an app nobody is using.
 *
 * `countedBy` names the array that decides how many areas are live, and doubles
 * as the source of their labels when it is `tabs`.
 *
 * Widgets absent from this record are walked permissively: every area, no
 * embedded app. Being too strict here loses real widgets silently, which is far
 * worse than reporting a dead one, so an unknown container gets the benefit of
 * the doubt. Add to the record when a container's rendering is known.
 *
 * See front-end/src/components/widgets/index.ts for what each widget declares.
 */
type TraversalRule = { countedBy?: 'areaSizes' | 'tabs'; embeddedApp?: boolean }

/** The widget that embeds another app. Named because the count of them is reported, not only walked through. */
const COMPONENT_WIDGET_NAME = 'Component'

const WIDGET_TRAVERSAL: Record<string, TraversalRule> = {
  SplitLayout: { countedBy: 'areaSizes' },
  Tab: { countedBy: 'tabs' },
  BBSideMenu: { countedBy: 'tabs' },
  [COMPONENT_WIDGET_NAME]: { embeddedApp: true },
}

type UniqueWidget = { nodes: Record<string, unknown>[]; paths: string[] }

/**
 * The widget options any scanner reads. Two placements that agree on these
 * produce the same report, whatever else differs between them.
 *
 * Everything outside this list is either presentation (`titleMenubar`,
 * `panelstyle`) or input the page reads at runtime (`configField:*`). A config
 * field can change what a page does — it might hold a URL — but the scan cannot
 * see that either way, which is why the values are reported per placement rather
 * than used to tell placements apart.
 */
const ANALYSIS_KEYS = [
  'html',
  'query',
  'targetGraph',
  'allowedRuleTags',
  'mutationLogsEnabled',
  'mutationLogsGraph',
  'mutationLogsCustomStatements',
]

/**
 * Depth-first over the widget tree, keeping every node rather than only the
 * Code Widgets. The rest are what let an empty report distinguish "this app has
 * no Code Widgets" from "nothing was read" or "the name I match on has moved".
 *
 * Two ways down. `areas` is the ordinary one. A Component widget also carries a
 * whole app of its own under component.appRef.appConfig, and in real configs
 * that is where the Code Widgets turn out to live — sometimes two apps deep, so
 * a walk that stops at the host app's own widgets reports nothing at all.
 *
 * Nodes can be null — a hole in a layout — and any node can carry areas.
 */
function collectWidgetNodes(node: unknown, prefix: string, embedded = false): FoundWidget[] {
  const record = asRecord(node)
  if (!record) return []

  /*
   * Every hop, written as `Type[index "label"]`.
   *
   * The type leads because it is what a reviewer recognises; the index and tab
   * name follow because they are what tells two placements apart. `mainArea` and
   * `areas` are dropped — they appear at every level and so distinguish nothing,
   * and keeping them made a deeply embedded path too long to read.
   *
   * Nothing is summarised away: a placement is identified by its whole trail,
   * and two placements inside the same embedded app differ only further down it.
   */
  const type = asString(record.widget) ?? 'widget'
  const here = prefix ? `${prefix} > ${type}` : type

  const rule = WIDGET_TRAVERSAL[type]

  /*
   * Only as many areas as the widget renders. The counting array missing
   * altogether means the config is not shaped the way the rule expects, so the
   * walk falls back to all of them rather than deciding the widget is empty.
   */
  const counter = rule?.countedBy ? record[rule.countedBy] : undefined
  const all = Array.isArray(record.areas) ? record.areas : []
  const areas = rule?.embeddedApp ? [] : Array.isArray(counter) ? all.slice(0, counter.length) : all

  /*
   * Tab and BBSideMenu label their areas positionally: tabs[i] names areas[i],
   * and that name is what a reviewer recognises from the interface. The same
   * array that counts them names them, so the two cannot disagree.
   */
  const labels = rule?.countedBy === 'tabs' && Array.isArray(counter) ? counter : []

  const fromAreas = areas.reduce<FoundWidget[]>((found, area, index) => {
    const label = asString(labels[index])?.trim()
    /*
     * The index goes on the container it indexes, not on the child sitting at it:
     * `Tab[2 "Map"] > Code Widget` reads "in this Tab, area 2, named Map", where
     * `Tab > Code Widget[2 "Map"]` reads as though the Code Widget had areas of
     * its own. Same information, and only one of the two can be read aloud.
     */
    const step = `${type}[${index}${label ? ` "${label}"` : ''}]`
    return found.concat(collectWidgetNodes(area, prefix ? `${prefix} > ${step}` : step, embedded))
  }, [{ node: record, path: here, embedded }])

  /*
   * Only a Component renders an embedded app, and it renders nothing else — its
   * own `areas`, where a config has them, are left from whatever the widget was
   * before. Guarding on the type is what stops the same page being reported
   * twice, once through the embedded app and once through a dead area.
   */
  if (!rule?.embeddedApp) return fromAreas

  const embeddedApp = asRecord(asRecord(asRecord(record.component)?.appRef)?.appConfig)
  if (!embeddedApp) return fromAreas

  /* Embedded apps are stored inline with no path or id, so the title is the only handle a reviewer gets. */
  const title = asString(embeddedApp.title)?.trim()
  /* Everything below here belongs to the embedded app, however many levels deeper it goes. */
  return fromAreas.concat(
    collectWidgetNodes(embeddedApp.mainArea, `${here} > component${title ? ` "${title}"` : ''}`, true)
  )
}

/**
 * Collapse widgets that are configured identically into one entry carrying
 * every path they were found at. A Component embedded twice, or an app whose
 * embedded copy sits alongside an inlined one, is one page to review — reported
 * once with its several locations rather than analyzed once per occurrence.
 *
 * Identity is the widget's whole stored config, so two pages count as the same
 * only when nothing about them differs.
 */
function groupByIdentity(found: FoundWidget[]): UniqueWidget[] {
  const byIdentity = new Map<string, UniqueWidget>()

  for (const widget of found) {
    const identity = stableStringify(Object.fromEntries(ANALYSIS_KEYS.map((key) => [key, widget.node[key]])))
    const existing = byIdentity.get(identity)
    if (existing) {
      existing.nodes.push(widget.node)
      existing.paths.push(widget.path)
    } else {
      byIdentity.set(identity, { nodes: [widget.node], paths: [widget.path] })
    }
  }

  return Array.from(byIdentity.values())
}

/** JSON with object keys in a fixed order, so identity does not depend on the order keys were written in. */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b))
    return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${stableStringify(item)}`).join(',')}}`
  }
  return JSON.stringify(value) ?? 'null'
}

function analyzeCodeWidget({ nodes, paths }: UniqueWidget): CodeWidgetAnalysis {
  /* The placements agree on everything the scan reads, so any of them answers for the page. */
  const node = nodes[0]
  const html = asString(node.html) ?? ''
  /*
   * Patterns run over a copy with bridge calls in one spelling and comments blanked; the locator reads the
   * original, which both rewrites keep character-aligned. The page is lexed once: blanking turns comments
   * into spaces and moves nothing, so the lex of the respelled page answers for the blanked one too.
   */
  const respelled = canonicalBridge(html)
  const lex = lexPage(respelled)
  const scanned = blank(respelled, lex.comments)
  const locate = makeLocator(html)
  const configuration = configurationFacts(nodes, html)

  const bridgeUsage = scanBridgeUsage(scanned, locate)
  const page = readPage(scanned, lex)
  const egress = scanEgress(page, locate)

  const findings = runCatalogue({
    html: scanned,
    page,
    raw: html,
    locate,
    egress,
    usage: bridgeUsage,
    configuration,
  })

  return {
    paths,
    widgetId: asString(node.id),
    title: asString(node.titleMenubar),
    htmlLines: html ? html.split('\n').length : 0,
    html,
    risks: riskVerdicts(findings),
    findings,
    bridgeUsage,
    egress,
    libraries: scanLibraries(egress),
    backendCalls: usedMembers(bridgeUsage, (entry) => Boolean(entry.callsBackend)),
    configuration,
  }
}

// --- bridge usage ----------------------------------------------------------

function scanBridgeUsage(html: string, locate: Locator): BridgeMemberUsage[] {
  return WISTOR_API.map((entry) => {
    const patterns = [
      new RegExp(`\\bwistor\\s*\\.\\s*${entry.member}\\b`, 'g'),
      /* Bounded, or WIDGET_CONFIG would be found inside WIDGET_CONFIG_FIELDS and every page would look like it reads its config. */
      ...(entry.aliases ?? []).map((alias) => new RegExp(`\\b${escapeRegExp(alias)}\\b`, 'g')),
    ]
    const evidence = patterns.flatMap((pattern) => matchAll(html, pattern).map((m) => locate(m.index)))
    const deduped = dedupeEvidence(evidence)

    return {
      member: entry.member,
      kind: entry.kind,
      description: entry.description,
      reachesBackend: reachesBackend(entry),
      used: deduped.length > 0,
      occurrences: evidence.length,
      evidence: deduped,
    }
  })
}

function usedMembers(usage: BridgeMemberUsage[], pick: (entry: WistorApiEntry) => boolean): WistorMember[] {
  return WISTOR_API.filter(pick)
    .map((entry) => entry.member)
    .filter((member) => usage.some((u) => u.member === member && u.used))
}

// --- egress ----------------------------------------------------------------

type EgressRule =
  | {
      kind: EgressKind
      /** The first capture group that took part in the match is the URL, when the rule captures one at all. */
      pattern: RegExp
      /** A match with no captured URL is a call with a computed target. */
      literal: boolean
    }
  /**
   * URLs in a tag's attributes, read with the browser's attribute rules (code-lexer startTags), so a
   * value is found quoted or not and an attribute named inside another's value is not mistaken for it.
   */
  | {
      kind: EgressKind
      tags: readonly string[]
      /** Attribute name → the URLs its value holds. */
      attributes: Record<string, (value: string) => string[]>
      /** Only tags whose attributes pass this. */
      when?: (attributes: Map<string, string>) => boolean
    }

const asUrl = (value: string) => [value]
/** `a.png 1x, b.png 2x` → each candidate's URL. */
const srcsetUrls = (value: string) =>
  value
    .split(',')
    .map((candidate) => candidate.trim().split(/\s+/)[0])
    .filter(Boolean)
/** `0; url=https://elsewhere` → the URL a refresh goes to. */
const refreshUrl = (value: string) => {
  const marker = /\burl\s*=/i.exec(value)
  const target = marker ? value.slice(marker.index + marker[0].length).trim().replace(/^['"]|['"]$/g, '') : ''
  return target ? [target] : []
}

const EGRESS_RULES: EgressRule[] = [
  { kind: 'fetch', pattern: /\bfetch\s*\(\s*['"`]([^'"`]*)['"`]/g, literal: true },
  { kind: 'fetch', pattern: /\bfetch\s*\(\s*(?!['"`])/g, literal: false },
  { kind: 'xhr', pattern: /\.open\s*\(\s*['"][A-Za-z]+['"]\s*,\s*['"`]([^'"`]*)['"`]/g, literal: true },
  { kind: 'xhr', pattern: /\bnew\s+XMLHttpRequest\b/g, literal: false },
  { kind: 'beacon', pattern: /\bsendBeacon\s*\(\s*['"`]([^'"`]*)['"`]/g, literal: true },
  { kind: 'beacon', pattern: /\bsendBeacon\s*\(\s*(?!['"`])/g, literal: false },
  { kind: 'websocket', pattern: /\bnew\s+WebSocket\s*\(\s*['"`]([^'"`]*)['"`]/g, literal: true },
  { kind: 'websocket', pattern: /\bnew\s+WebSocket\s*\(\s*(?!['"`])/g, literal: false },
  { kind: 'eventsource', pattern: /\bnew\s+EventSource\s*\(\s*['"`]([^'"`]*)['"`]/g, literal: true },
  { kind: 'dynamic-import', pattern: /\bimport\s*\(\s*['"`]([^'"`]*)['"`]/g, literal: true },
  { kind: 'dynamic-import', pattern: /\bimport\s*\(\s*(?!['"`])/g, literal: false },
  { kind: 'script-src', tags: ['script'], attributes: { src: asUrl } },
  { kind: 'link-href', tags: ['link'], attributes: { href: asUrl } },
  { kind: 'img-src', tags: ['img'], attributes: { src: asUrl, srcset: srcsetUrls } },
  { kind: 'img-src', tags: ['input'], attributes: { src: asUrl }, when: (attributes) => attributes.get('type')?.toLowerCase() === 'image' },
  { kind: 'media-src', tags: ['video'], attributes: { src: asUrl, poster: asUrl } },
  { kind: 'media-src', tags: ['audio', 'track'], attributes: { src: asUrl } },
  { kind: 'media-src', tags: ['source'], attributes: { src: asUrl, srcset: srcsetUrls } },
  { kind: 'object-src', tags: ['embed'], attributes: { src: asUrl } },
  { kind: 'object-src', tags: ['object'], attributes: { data: asUrl } },
  { kind: 'iframe-src', tags: ['iframe', 'frame'], attributes: { src: asUrl } },
  { kind: 'form-action', tags: ['form'], attributes: { action: asUrl } },
  { kind: 'form-action', tags: ['button', 'input'], attributes: { formaction: asUrl } },
  /*
   * Every run in these patterns ends on a character the next part cannot start with, so a failed match is
   * given up in one pass. `url\(\s*["']?([^"')]+)…` let the spaces belong to three parts at once, and five
   * thousand spaces after a `url(` froze the tab for seventeen seconds.
   */
  { kind: 'css-url', pattern: /\burl\(\s*(?:"([^"]*)"|'([^']*)'|([^"'()\s]+))\s*\)/gi, literal: true },
  { kind: 'css-import', pattern: /@import\s*(?:url\(\s*)?["']([^"']+)["']/gi, literal: true },
  { kind: 'open-url', pattern: /\bwistor\s*\.\s*openUrl\s*\(\s*['"`]([^'"`]*)['"`]/g, literal: true },
  { kind: 'open-url', pattern: /\bwistor\s*\.\s*openUrl\s*\(\s*(?!['"`])/g, literal: false },

  /*
   * Navigation. Reported on the attempt, not the outcome: the frame is
   * sandboxed, so some of these are refused at runtime, and a page written to
   * navigate is worth a builder's attention whether it fails or succeeds —
   * broken and escaping are both answers nobody wants.
   *
   * `\blocation` deliberately covers window.location, document.location,
   * top.location and parent.location in one rule. The `=(?!=)` guard keeps
   * comparisons from reading as assignments.
   */
  { kind: 'location-assign', pattern: /\blocation\s*(?:\.\s*href\s*)?=(?!=)\s*['"`]([^'"`]*)['"`]/g, literal: true },
  { kind: 'location-assign', pattern: /\blocation\s*(?:\.\s*href\s*)?=(?!=)\s*(?!['"`])/g, literal: false },
  { kind: 'location-assign', pattern: /\blocation\s*\.\s*(?:assign|replace)\s*\(\s*['"`]([^'"`]*)['"`]/g, literal: true },
  { kind: 'location-assign', pattern: /\blocation\s*\.\s*(?:assign|replace)\s*\(\s*(?!['"`])/g, literal: false },
  { kind: 'window-open', pattern: /\b(?:window|self)\s*\.\s*open\s*\(\s*['"`]([^'"`]*)['"`]/g, literal: true },
  { kind: 'window-open', pattern: /\b(?:window|self)\s*\.\s*open\s*\(\s*(?!['"`])/g, literal: false },
  { kind: 'anchor-href', tags: ['a', 'area'], attributes: { href: asUrl } },
  {
    kind: 'meta-refresh',
    tags: ['meta'],
    attributes: { content: refreshUrl },
    when: (attributes) => attributes.get('http-equiv')?.trim().toLowerCase() === 'refresh',
  },

  /*
   * Assigning a src in script. `new Image().src = 'https://host/?d=' + secret'`
   * is a request with a payload on it, and nothing in the markup rules sees it
   * because the element never appears in the source.
   */
  { kind: 'dynamic-src', pattern: /\.\s*src(?:set)?\s*=(?!=)\s*['"`]([^'"`]*)['"`]/g, literal: true },
  { kind: 'dynamic-src', pattern: /\.\s*src(?:set)?\s*=(?!=)\s*(?![\s'"`])/g, literal: false },

  /*
   * fetch and sendBeacon held under another name or bound: every later call through that name sends to a
   * target no rule reads. Anchored on the `=` or the bind, so the word "fetch" in prose does not count.
   */
  {
    kind: 'fetch',
    pattern: /=(?<![=!<>]=)(?![=>])\s*(?:window\s*\.\s*)?fetch\b(?!\s*\()|\bfetch\s*\.\s*(?:bind|call|apply)\s*\(/g,
    literal: false,
  },
  {
    kind: 'beacon',
    pattern:
      /=(?<![=!<>]=)(?![=>])\s*(?:window\s*\.\s*)?navigator\s*\.\s*sendBeacon\b(?!\s*\()|\bsendBeacon\s*\.\s*(?:bind|call|apply)\s*\(/g,
    literal: false,
  },

  /*
   * The same channel through an href. `a.href = 'https://host/?d=' + secret` is
   * a link with a payload on it, and the markup rule never sees it because the
   * element carries no href in the source.
   *
   * The lookbehind keeps `location.href` out, which the location rules already
   * report — without it every navigation would appear twice in the inventory.
   */
  { kind: 'dynamic-href', pattern: /(?<!location)\.\s*href\s*=(?!=)\s*['"`]([^'"`]*)['"`]/g, literal: true },
  { kind: 'dynamic-href', pattern: /(?<!location)\.\s*href\s*=(?!=)\s*(?!['"`])/g, literal: false },

  /* An XHR whose address is a variable: `new XMLHttpRequest` says one exists, this says it goes somewhere unread. */
  { kind: 'xhr', pattern: /\.open\s*\(\s*['"][A-Za-z]+['"]\s*,\s*(?!['"`])/g, literal: false },
]

/** Kinds that move the person or the frame, rather than fetching in the background. */
const NAVIGATION_KINDS: EgressKind[] = [
  'location-assign',
  'window-open',
  'anchor-href',
  'dynamic-href',
  'meta-refresh',
  'open-url',
]

function scanEgress(page: Page, locate: Locator): EgressTarget[] {
  const html = page.text
  const target = (kind: EgressKind, url: string | null, literal: boolean, index: number): EgressTarget => {
    const urlClass = kind === 'open-url' && opensInPlace(page, index) ? 'same-origin' : literal ? classifyUrl(url, kind) : 'dynamic'
    return { kind, url, host: urlClass === 'remote' ? hostOf(url) : null, urlClass, index, evidence: locate(index) }
  }

  const tagNames = new Set(['base', ...EGRESS_RULES.flatMap((rule) => ('tags' in rule ? rule.tags : []))])
  const tags = startTags(html, (name) => tagNames.has(name))

  const targets = EGRESS_RULES.flatMap((rule) => {
    if ('tags' in rule) {
      return tags.flatMap((tag) => {
        if (!rule.tags.includes(tag.name) || (rule.when && !rule.when(tag.attributes))) return []
        return Object.entries(rule.attributes).flatMap(([name, urlsOf]) => {
          const value = tag.attributes.get(name)
          return value === undefined ? [] : urlsOf(value).map((url) => target(rule.kind, url, true, tag.index))
        })
      })
    }
    return matchAll(html, rule.pattern).map((match) =>
      target(rule.kind, rule.literal ? firstCapture(match) : null, rule.literal, match.index)
    )
  })

  /* <base href> moves every relative address to its host: fetch('c?d=' + d) then goes wherever the base points. */
  const base = tags.find((tag) => tag.name === 'base' && tag.attributes.has('href'))?.attributes.get('href') ?? null
  const baseHost = base && classifyUrl(base) === 'remote' ? hostOf(base) : null
  if (!baseHost) return targets
  return targets.map((entry) =>
    entry.urlClass === 'relative' && !URL_SCHEME.test(entry.url ?? '') ? { ...entry, urlClass: 'remote', host: baseHost } : entry
  )
}

/**
 * `wistor.openUrl(url, { inPlace: true })` at `index`. Only a literal `true` counts: options held in a
 * variable are read as a new tab, which is judged by the URL alone.
 */
function opensInPlace(page: Page, index: number): boolean {
  const options = topLevelArguments(page, page.text.indexOf('(', index))[1]
  return options !== undefined && IN_PLACE_OPTION.test(page.text.slice(...options))
}

const IN_PLACE_OPTION = /^\s*\{(?:[^{}]*,)?\s*['"]?inPlace['"]?\s*:\s*true\b/

function firstCapture(match: RegExpExecArray): string | null {
  return match.slice(1).find((group) => group !== undefined) ?? null
}

/**
 * No URL parser here on purpose: the target may be a template with placeholders
 * in it, which `new URL()` would reject, and a target that cannot be parsed is
 * exactly the kind a reviewer needs to see rather than have dropped.
 */
function classifyUrl(url: string | null, kind?: EgressKind): UrlClass {
  if (url == null || url.trim() === '') return 'dynamic'
  const value = url.trim()
  /* A fixed mailto:, tel: or sms: link carries only what is written in it. */
  if (kind === 'anchor-href' && /^(?:mailto|tel|sms):/i.test(value)) return 'relative'
  if (/^(?:data|blob):/i.test(value)) return 'inline'
  /* The browser reads \ as / in an address, so /\host and \\host are protocol-relative like //host. */
  if (/^[/\\]{2}/.test(value)) return 'remote'
  if (/^javascript:/i.test(value)) return 'dynamic'
  /* Any scheme leaves the page: https:/host (one slash is enough), mailto:, and the rest. */
  if (URL_SCHEME.test(value)) return 'remote'
  return 'relative'
}

const URL_SCHEME = /^\s*[a-z][a-z0-9+.-]*:/i

/** The host an address reaches, read past backslashes, a single slash and user@ as the browser does. */
function hostOf(url: string | null): string | null {
  if (url == null) return null
  const location = parsedUrl(url)
  if (location?.hostname) return location.hostname
  /* Not a parseable URL — a template with placeholders, or mailto:x@host — so read the host by hand. */
  const authority = url.trim().match(/^(?:[a-z][a-z0-9+.-]*:)?[/\\]*([^/?#\\]+)/i)?.[1]
  return authority ? authority.slice(authority.lastIndexOf('@') + 1) : null
}

// --- libraries -------------------------------------------------------------

type CdnRule = { host: RegExp; path: RegExp }

/** Capture group 1 is the package name, group 2 the version where the CDN puts one in the path. */
const CDN_RULES: CdnRule[] = [
  { host: /(^|\.)unpkg\.com$/i, path: /^\/((?:@[^/@]+\/)?[^/@]+)(?:@([^/]+))?/ },
  { host: /(^|\.)jsdelivr\.net$/i, path: /^\/npm\/((?:@[^/@]+\/)?[^/@]+)(?:@([^/]+))?/ },
  { host: /(^|\.)cdnjs\.cloudflare\.com$/i, path: /^\/ajax\/libs\/([^/]+)\/([^/]+)/ },
  { host: /(^|\.)esm\.sh$/i, path: /^\/((?:@[^/@]+\/)?[^/@]+)(?:@([^/]+))?/ },
]

const LIBRARY_KINDS: EgressKind[] = ['script-src', 'link-href', 'dynamic-import', 'css-import']

/**
 * What the page pulls in from elsewhere, named where the URL allows it. Read off
 * the egress inventory rather than scanned separately: a library is a remote
 * fetch that happens to be a package, and deriving it keeps the two consistent.
 *
 * Only what is loaded by URL. A library pasted into the page inline is invisible
 * here, and so is one loaded under a name built at runtime.
 */
function scanLibraries(egress: EgressTarget[]): Library[] {
  const loads = egress.filter(
    (target) => LIBRARY_KINDS.includes(target.kind) && target.urlClass === 'remote' && target.url
  )

  return loads.map((target) => {
    const url = target.url as string
    const path = url.replace(/^(?:[a-z][a-z0-9+.-]*:)?\/\/[^/]+/i, '')
    const rule = CDN_RULES.find((candidate) => candidate.host.test(target.host ?? ''))
    const match = rule ? path.match(rule.path) : null

    return {
      name: match?.[1] ?? path.split('/').filter(Boolean).pop() ?? (target.host ?? url),
      version: match?.[2] ?? null,
      url,
      evidence: target.evidence,
    }
  })
}

// --- credential-seeking inputs ---------------------------------------------

/**
 * Words that make a field look like it asks for a secret. Dutch alongside
 * English because the apps are Dutch and a scan that only knows "password"
 * would miss every field a Dutch builder actually writes.
 */
const CREDENTIAL_WORDS = new RegExp(
  [
    /* password, passwd, passphrase, passcode; pass and pwd only as a word of their own, so passengers and compass are not */
    /pass(?:word|wd|phrase|code)|(?<![a-z])pass(?![a-z])|(?<![a-z])pwd(?![a-z])/,
    /wachtwoord|pincode|secret(?!ar)|credential|api[-_\s]?key/,
    /* token as a word or the end of one: auth_token, csrfToken, accessToken — not tokenize */
    /token(?![a-z])/,
    /creditcard|card[-_\s]?number|(?<![a-z])cvv(?![a-z])|(?<![a-z])iban(?![a-z])|(?<![a-z])bsn(?![a-z])|burgerservicenummer/,
  ]
    .map((part) => part.source)
    .join('|'),
  'i'
)

/** Weaker signals: ordinary on their own, part of a login form when a secret field sits with them. */
const IDENTITY_WORDS = /username|gebruikersnaam|user[-_\s]?id|e-?mail|\blogin\b|inloggen|aanmelden|wachtwoord vergeten/i

/**
 * An element made editable, unless it says contenteditable="false". Tested on one tag's text at a time,
 * never on the page: run over the page, `[^>]*` on both sides of the attribute retried every split point
 * from every `<`, and a few kilobytes of `<a contenteditable ` without a `>` hung the browser.
 */
const EDITABLE_ATTRIBUTE = /\bcontenteditable\b(?!\s*=\s*["']?false)/i

/** Label words strong enough to mark an editable element as a secret field, without a name to go on. */
const SECRET_LABEL = /password|passwd|wachtwoord|pincode|passcode/i

/** How far before an editable element its label may sit: one short label and its markup, not the paragraph above. */
const LABEL_REACH_CHARS = 120

/** Names a value forwarded out of the page should never carry. Stricter than CREDENTIAL_WORDS: no bare "pass", which is in "passenger". */
const SECRET_VALUE = /\bpsw\b|password|passwd|wachtwoord|pincode|passcode|\bsecret\b|api[-_]?key|token(?![a-z])|\bbsn\b|\biban\b/i

/**
 * The bridge under another name. Anchored on the `=` (or on bind/call/apply, a spread, a computed key) rather
 * than on the name: a pattern starting at every identifier took 10 s on 100 KB of `a.a.a…`. The lookbehinds
 * come after the `=`, so they run only where one is, never from every space of a long run. They
 * leave the page skeleton alone — `var wistor = window.wistor || {…}` and `window.wistor = …` assign to the
 * bridge, and `===` compares with it. Runs over canonicalBridge output, so a `[` left after wistor is computed.
 */
const BRIDGE_INDIRECT =
  /=(?<![=!<>]=)(?<!\bwistor\s*=)(?<!\bwindow\s*\.\s*wistor\s*=)(?![=>])\s*(?:window\s*\.\s*)?wistor\b(?!\s*[.[])|\.\s*(?:bind|call|apply)\s*\(\s*(?:window\s*\.\s*)?wistor\b|\.{3}\s*(?:window\s*\.\s*)?wistor\b|\bwistor[ \t]*(?:\?\.[ \t]*)?\[/g

/**
 * Code the source does not show: eval and its relatives, string timers, globals looked up by a computed name, and
 * script elements created at runtime. The computed lookup is the usual way past every other check here.
 */
const DYNAMIC_CODE = new RegExp(
  [
    /* eval anywhere: called, (0, eval)(p), Reflect.apply(eval, …), handed on. */
    /(?<![.\w$])eval\b/,
    /* The Function constructor with any argument, new or not. */
    /(?<![.\w$])(?:new\s+)?Function\s*\(/,
    /* The same constructor reached through any object: [].constructor.constructor, x['constructor']. */
    /\bconstructor\s*\.\s*constructor\b|\[\s*['"`]constructor['"`]\s*\]/,
    /* A timer handed a string, which it runs as code. */
    /\b(?:setTimeout|setInterval)\s*\(\s*['"`]/,
    /* Any bracket lookup on a global: a computed name is how every other check is walked around. window['wistor'] was rewritten to window.wistor before this runs. */
    /(?<![.\w$])(?:window|self|globalThis|document|top|parent|frames)\s*(?:\?\.\s*)?\[/,
    /* A script element, in any case. */
    /\bcreateElement\s*\(\s*['"`][sS][cC][rR][iI][pP][tT]['"`]/,
    /* Markup written in as a whole document or fragment, which runs its scripts and handlers. */
    /\bdocument\s*\.\s*write(?:ln)?\s*\(|\.\s*srcdoc\s*=(?!=)|\bcreateContextualFragment\s*\(/,
    /* A module whose code is the URL itself. */
    /\bimport\s*\(\s*['"`](?:data|blob):/,
  ]
    .map((part) => part.source)
    .join('|'),
  'g'
)

/** A numeric or named character reference, as the browser decodes it in an attribute. */
const CHARACTER_REFERENCE = /&(?:#(?:x[0-9a-f]+|[0-9]+);?|[a-z][a-z0-9]*;)/gi

/** A tag with an event handler, as written inside a string: `'<img src=x onerror="' + p + '">'`. */
const TAG_WITH_HANDLER = /<[a-z][^<>]*\son[a-z]+\s*=/i

/** Globals a page may also use as an ordinary variable name; a lookup on one the page declares itself is not a global lookup. */
const SHADOWABLE_GLOBALS = /^(?:self|top|parent|frames)$/

const EMBEDDED_MEDIA = /\bdata:(?:image|audio|video)\//gi

/** The user-management graph and vocabulary, in both generations, and the password predicate they share. */
const ACCOUNT_DATA = /user-?management|[#:/]psw\b/gi

/** A group holding only one fully unbound triple pattern: { ?s ?p ?o } and GRAPH ?g { ?s ?p ?o }. */
const UNBOUNDED_TRIPLE = /\{\s*\?\w+\s+\?\w+\s+\?\w+\s*(?:\.\s*)?\}/g

/** Text meant for the review rather than the person using the page, in English and Dutch. */
const REVIEWER_ADDRESS = new RegExp(
  [
    /* "ignore (all) (previous) instructions": instructions need no qualifier, they are only ever the reader's */
    /\b(?:ignore|disregard|override|negeer)\s+(?:(?:all|any|the|de|alle)\s+)?(?:(?:previous|prior|above|earlier|vorige|eerdere)\s+)?(?:instructions|instructies)\b/,
    /* rules only with a qualifier: "override rules for this object" is a feature, "ignore all previous rules" is not */
    /\b(?:ignore|disregard|override|negeer)\s+(?:(?:all|any|alle)\s+(?:(?:the|de|previous|prior|above|earlier|vorige|eerdere)\s+)?|(?:(?:the|de)\s+)?(?:previous|prior|above|earlier|vorige|eerdere)\s+)(?:rules|regels)\b/,
    /* a reviewing agent named as such; "model" only after review, so "security model" is not one */
    /\b(?:security|audit)\s+(?:agent|bot|assistant|llm)\b|\breview(?:er|ing)?\s+(?:agent|bot|model|assistant|llm)\b/,
    /* claimed approval, only near a word for the review: "Pre-approved" on its own is a status label */
    /\b(?:security|review|scan|audit|check|controle)[^<>\n]{0,60}?\b(?:pre-?approved|al\s+goedgekeurd)\b/,
    /\b(?:pre-?approved|al\s+goedgekeurd)\b[^<>\n]{0,60}?\b(?:security|review|scan|audit|check|controle)/,
    /* a greeting to the machine */
    /\b(?:dear|hello|hi|note\s+to\s+the)\s+(?:ai|llm|reviewer|assistant)\b/,
  ]
    .map((part) => part.source)
    .join('|'),
  'gi'
)
const PASSWORD_TYPE = /type\s*=\s*["']password["']/i

/*
 * The reasoning behind treating these as serious rather than informational: the
 * platform owns authentication. A Code Widget is never the thing that should be
 * collecting a password, so a password field inside one is either a mistake or
 * an imitation of the real login, and both are worth a builder's attention. The
 * page is sandboxed but it renders whatever it likes, and a convincing "your
 * session expired" panel costs nothing to write.
 */

// --- backend load ----------------------------------------------------------

const BACKEND_MEMBERS = WISTOR_API.filter((entry) => entry.callsBackend).map((entry) => entry.member)

/** Where a secret a page collected can go once it has it: written to the store, or forwarded out of the page. */
const CREDENTIAL_SINKS: WistorMember[] = ['updateQuads', 'uploadRdf', 'uploadFile', 'setParam', 'setParams', 'openUrl']

const BACKEND_CALL_PATTERN = new RegExp(`\\bwistor\\s*\\.\\s*(${BACKEND_MEMBERS.join('|')})\\s*\\(`, 'g')

type BackendHit = { member: string; index: number }

// --- page structure ---------------------------------------------------------

/**
 * Where the page builds or looks up code at runtime: DYNAMIC_CODE in script code, and string literals that
 * hold a tag with a handler. Script code only, so prose that says "eval" and onclick="…" in markup are not
 * counted; a bracket lookup on a name the page declares itself (`var parent = node.parent`) is ordinary.
 */
function dynamicCode(page: Page): number[] {
  const inCode = (index: number) => codeRangeAt(page.lex, index) !== null
  /* Once per name, not per lookup: a page of a thousand parent[…] reads the page once. */
  const declared = new Map<string, boolean>()
  const isDeclared = (name: string) => declared.get(name) ?? declared.set(name, declaresName(page.text, name)).get(name)
  const patternHits = matchAll(page.text, DYNAMIC_CODE)
    .filter((match) => inCode(match.index))
    .filter((match) => {
      const name = match[0].match(/^(?:self|top|parent|frames)/)?.[0]
      return !name || !SHADOWABLE_GLOBALS.test(name) || !isDeclared(name)
    })
    .map((match) => match.index)

  const handlerStrings: number[] = []
  for (const { start, end } of page.lex.codeRanges) {
    for (let index = start; index < end; index++) {
      const after = page.lex.literalEnd[index]
      if (after <= 0) continue
      if (page.text[index] !== '/' && TAG_WITH_HANDLER.test(page.text.slice(index, after))) handlerStrings.push(index)
      index = after - 1
    }
  }
  /* A script loaded from a data: or blob: address runs code that is nowhere in the page's source. */
  const urlScripts = startTags(page.text, (name) => name === 'script')
    .filter((tag) => /^\s*(?:data|blob):/i.test(tag.attributes.get('src') ?? ''))
    .map((tag) => tag.index)

  /*
   * Handler code is scanned as written, but the browser decodes it first, so wistor&#46;updateQuads runs as
   * wistor.updateQuads. Quotes, &, < and > have a reason to be encoded in an attribute; nothing else a page
   * needs is written as a character reference in JavaScript.
   */
  const encodedInHandlers = page.lex.codeRanges
    .filter((range) => range.event !== null)
    .flatMap(({ start, end }) =>
      matchAll(page.text.slice(start, end), CHARACTER_REFERENCE)
        .filter((reference) => !/^["'&<>\s]?$/.test(decodeHTML(reference[0])))
        .map((reference) => start + reference.index)
    )

  return [...patternHits, ...handlerStrings, ...urlScripts, ...encodedInHandlers].sort((a, b) => a - b)
}

/** Whether the page declares `name` itself, as a variable or a parameter. */
function declaresName(text: string, name: string): boolean {
  if (new RegExp(`\\b(?:var|let|const)\\s+${name}\\b|(?<![\\w$.])${name}\\s*=>`).test(text)) return true
  /* Each parameter list is read once and split, rather than searched for the name with a pattern that backtracks. */
  return matchAll(text, PARAMETER_LIST).some((match) =>
    (match[1] ?? match[2]).split(',').some((parameter) => parameter.split('=')[0].trim() === name)
  )
}

/** `function name(a, b)` and `(a, b) =>`: capture 1 or 2 is the parameter list. */
const PARAMETER_LIST = /\bfunction\b[^(]{0,80}\(([^()]*)\)|\(([^()]*)\)\s*=>/g

/** A page's text with its lexer tables. Everything that asks where a bracket, call or body ends reads these. */
type Code = { text: string; lex: PageLex }

/**
 * What the detectors ask about a page's structure, worked out once per page. Built eagerly and passed
 * around, never looked up by the page's text: a cache keyed on a page-sized string costs a full string
 * compare per lookup, which made every bracket question as slow as reading the page.
 */
export type Page = Code & {
  functions: Map<string, NamedFunction>
  /** Where each name is used in script code: outside strings and comments, and not as a property (`x.name`) or object key. */
  references: Map<string, number[]>
  /** Sorted, non-overlapping ranges of code that runs from a click or submit. */
  userTriggered: [number, number][]
}

function readPage(text: string, lex: PageLex): Page {
  const code = { text, lex }
  const functions = namedFunctions(code)
  const references = nameReferences(code)
  return { ...code, functions, references, userTriggered: userTriggeredRegions(code, functions, references) }
}

/** One pass over the page's code: every identifier, by name, skipping literals and property names. */
function nameReferences(code: Code): Map<string, number[]> {
  const { text, lex } = code
  const references = new Map<string, number[]>()
  for (const { start, end } of lex.codeRanges) {
    for (let index = start; index < end; index++) {
      if (lex.literalEnd[index] > 0) {
        index = lex.literalEnd[index] - 1
        continue
      }
      if (!IDENTIFIER_START.test(text[index]) || (index > 0 && IDENTIFIER_PART.test(text[index - 1]))) continue
      let after = index + 1
      while (after < end && IDENTIFIER_PART.test(text[after])) after++
      let before = index - 1
      while (before >= start && /\s/.test(text[before])) before--
      const next = skipSpace(text, after)
      /* `x.name` is a property and `{ name: … }` a key, not uses of the name. */
      const isKey = text[next] === ':' && (text[before] === '{' || text[before] === ',')
      if (text[before] !== '.' && !isKey) {
        const name = text.slice(index, after)
        const positions = references.get(name)
        if (positions) positions.push(index)
        else references.set(name, [index])
      }
      index = after - 1
    }
  }
  return references
}

const IDENTIFIER_START = /[A-Za-z_$]/
const IDENTIFIER_PART = /[\w$]/

/**
 * Index of every updateQuads call that deletes. Confined to code — scripts and event-handler attributes — since
 * a heading or paragraph that documents the API ("wistor.updateQuads({ inserts, deletes })") is prose.
 *
 * Only a plain object literal is read: no spread and no computed key. Anything else — a variable,
 * `{ ...p }`, `Object.assign({}, p)` — counts as deleting whenever the page's code mentions `deletes` or
 * builds a property name from strings (`p['del' + 'etes']`). A computed key in the payload itself counts on
 * its own: the scan cannot say it is not `deletes`.
 */
function deletingWrites(code: Code): number[] {
  const html = code.text
  const scriptText = code.lex.codeRanges.map(({ start, end }) => html.slice(start, end)).join('\n')
  const codeMentionsDeletes = /\bdeletes\b/.test(scriptText)
  const keyBuiltFromStrings = /\[\s*(?:['"`][^'"`\]]*['"`]\s*\+|[A-Za-z_$][\w$]*\s*\+\s*['"`])/.test(scriptText)

  return matchAll(html, /\bwistor\s*\.\s*updateQuads\s*\(/g).flatMap((match) => {
    if (!codeRangeAt(code.lex, match.index)) return []
    const start = html.indexOf('(', match.index)
    const end = balancedEnd(code, start)
    /* An unclosed call is not readable; reading on to the end of the script for each one would be quadratic. */
    if (end < 0) return codeMentionsDeletes || keyBuiltFromStrings ? [match.index] : []
    const args = html.slice(start, end)
    /* A `[` opening a key, whose partner is followed by `:`. The `:` keeps an array of arrays (inserts: [[a, b]]) out. */
    const computedKey = matchAll(args, /[{,]\s*\[/g).some((key) => {
      const after = balancedEnd(code, start + key.index + key[0].length - 1)
      return after > 0 && after <= end && /^\s*:/.test(html.slice(after, after + 16))
    })
    const readable = /^\(\s*\{/.test(args) && !args.includes('...') && !computedKey
    const deletes =
      /\bdeletes\b/.test(args) || computedKey || (!readable && (codeMentionsDeletes || keyBuiltFromStrings))
    return deletes ? [match.index] : []
  })
}

/** Click and submit handlers: the registration, and the region holding the handler or the name it hands over. */
const USER_ACTION_OPENERS = [
  /\baddEventListener\s*\(\s*['"](?:click|submit)['"]\s*,/g,
  /\.\s*on(?:click|submit)\s*=/g,
]

/**
 * Where the page calls one bridge member, in script code. The pattern alone would also match the member
 * named in a string or a comment, which is how a page that only documents a call reads as making one.
 */
function memberCalls(code: Code, member: WistorMember): number[] {
  const pattern = new RegExp(`\\bwistor\\s*\\.\\s*${member}\\s*\\(`, 'g')
  return matchAll(code.text, pattern)
    .filter((match) => codeRangeAt(code.lex, match.index))
    .map((match) => match.index)
}

/** Whether the code at `index` runs only from a click or submit: in a handler, or in a click-only function. */
function isUserTriggered(page: Page, index: number): boolean {
  return inRanges(page.userTriggered, index)
}

/**
 * Code that runs only from a click or submit: the handler regions — registered in script, or written as
 * onclick/onsubmit attributes — plus the bodies of the click-only functions.
 *
 * A function is click-only when a handler calls it or hands it over by name (directly or through other
 * click-only functions), and every other use of its name sits inside a handler or another click-only
 * function. A call on load, or the name handed to setTimeout or onData, rules it out: the same delete then
 * also runs without anyone clicking. Mentions only count from script code, outside strings, so
 * id="save" and getElementById("save") are not uses of save().
 */
function userTriggeredRegions(
  code: Code,
  functions: Map<string, NamedFunction>,
  references: Map<string, number[]>
): [number, number][] {
  const text = code.text
  const attributeHandlers = code.lex.codeRanges
    .filter((range) => range.event === 'click' || range.event === 'submit')
    .map(({ start, end }): [number, number] => [start, end])
  const registered = userActionRegions(code)
  const handlers = mergeRanges([...registered, ...attributeHandlers])
  /* addEventListener('click', save) and onclick = save: the handler region is the name alone. */
  const handedOver = new Set(
    registered
      .filter(([from, to]) => /^[A-Za-z_$][\w$]*$/.test(text.slice(from, to).trim()))
      .map(([from]) => skipSpace(text, from))
  )
  const isCallAt = (at: number, name: string) =>
    /^\s*(?:\.\s*(?:call|apply)\s*)?\(/.test(text.slice(at + name.length, at + name.length + 24))

  /* For each use of each function's name: is it in a handler, and which function bodies hold it. */
  const bodies = [...functions.values()]
  type Use = { inHandler: boolean; callsOrHandsOver: boolean; containers: string[] }
  const uses = new Map<string, Use[]>(bodies.map((fn) => [fn.name, []]))
  /* Names used inside each function's body: the ones to recheck when that function stops being click-only. */
  const usedInside = new Map<string, Set<string>>(bodies.map((fn) => [fn.name, new Set()]))
  const positions = bodies
    .flatMap((fn) => (references.get(fn.name) ?? []).filter((at) => at !== fn.nameAt).map((at) => ({ at, fn })))
    .sort((a, b) => a.at - b.at)
  /* One sweep in page order with the bodies currently open on a stack: bodies nest or are disjoint. */
  const opening = [...bodies].sort((a, b) => a.body[0] - b.body[0])
  const open: NamedFunction[] = []
  let next = 0
  for (const { at, fn } of positions) {
    while (next < opening.length && opening[next].body[0] <= at) open.push(opening[next++])
    for (let top = open.length - 1; top >= 0; top--) if (open[top].body[1] <= at) open.splice(top, 1)
    const containers = open.map((holder) => holder.name)
    for (const holder of containers) usedInside.get(holder)?.add(fn.name)
    uses.get(fn.name)?.push({ inHandler: inRanges(handlers, at), callsOrHandsOver: isCallAt(at, fn.name) || handedOver.has(at), containers })
  }

  /* Start from every function a handler could call, and drop each one with a use outside, until none changes. */
  const callable = (name: string) => (uses.get(name) ?? []).some((use) => use.callsOrHandsOver)
  const clickOnly = new Set(bodies.map((fn) => fn.name).filter(callable))
  const holds = (name: string) =>
    callable(name) &&
    (uses.get(name) ?? []).every((use) => use.inHandler || use.containers.some((holder) => clickOnly.has(holder)))
  const pending = [...clickOnly]
  while (pending.length) {
    const name = pending.pop() as string
    if (!clickOnly.has(name) || holds(name)) continue
    clickOnly.delete(name)
    for (const used of usedInside.get(name) ?? []) if (clickOnly.has(used)) pending.push(used)
  }

  return mergeRanges([...handlers, ...[...clickOnly].map((name) => functions.get(name)?.body as [number, number])])
}

function inRanges(ranges: [number, number][], index: number): boolean {
  return rangeIndexAt(ranges, index, (range) => range) >= 0
}

/** Sorted, non-overlapping ranges covering the same characters, so a lookup is one binary search. */
function mergeRanges(ranges: [number, number][]): [number, number][] {
  const sorted = [...ranges].sort((a, b) => a[0] - b[0])
  const merged: [number, number][] = []
  for (const [from, to] of sorted) {
    const last = merged[merged.length - 1]
    if (last && from <= last[1]) last[1] = Math.max(last[1], to)
    else merged.push([from, to])
  }
  return merged
}

function userActionRegions(code: Code): [number, number][] {
  const html = code.text
  return USER_ACTION_OPENERS.flatMap((opener) =>
    matchAll(html, opener).map((match) => inlineHandlerRegion(code, match.index + match[0].length))
  )
}

/**
 * The region a handler registration hands control to, starting just after the opener (the comma of
 * addEventListener, or the `=` of onclick). An inline function or arrow gives its body, braced or a bare
 * expression; a bare name (`addEventListener('click', save)`) gives the name's span, so its function body
 * is picked up through userTriggeredRegions.
 */
function inlineHandlerRegion(code: Code, from: number): [number, number] {
  const html = code.text
  const start = skipSpace(html, from)
  /* Not a function: a bare name or another expression — its span, so a named function is picked up through userTriggeredRegions. */
  return functionAt(code, start) ?? [start, endOfExpression(code, start)]
}

/**
 * The body of the function written at `at` — `function name(…) {…}`, `(…) => …` or `x => …`, optionally async —
 * as a range, or null when no function starts there. An arrow's bare expression body ends where the expression
 * does. Reads brackets through the lexer's table, so it costs the length of the head, not of the page.
 */
function functionAt(code: Code, at: number): [number, number] | null {
  const html = code.text
  let index = skipSpace(html, at)
  if (html.startsWith('async', index) && /\s/.test(html[index + 5] ?? '')) index = skipSpace(html, index + 5)

  const arrow = !(html.startsWith('function', index) && !/[\w$]/.test(html[index + 8] ?? ''))
  if (!arrow) {
    index = skipSpace(html, index + 8)
    if (html[index] === '*') index = skipSpace(html, index + 1)
    while (index < html.length && /[\w$]/.test(html[index])) index++
    index = skipSpace(html, index)
  }

  if (html[index] === '(') {
    const after = balancedEnd(code, index)
    if (after < 0) return null
    index = skipSpace(html, after)
  } else if (arrow && /[A-Za-z_$]/.test(html[index] ?? '')) {
    while (index < html.length && /[\w$]/.test(html[index])) index++
    index = skipSpace(html, index)
  } else {
    return null
  }

  if (arrow) {
    if (!html.startsWith('=>', index)) return null
    index = skipSpace(html, index + 2)
  }
  if (html[index] === '{') {
    const end = balancedEnd(code, index)
    return end > 0 ? [index, end] : null
  }
  return arrow ? [index, endOfExpression(code, index)] : null
}

function skipSpace(html: string, index: number): number {
  while (index < html.length && /\s/.test(html[index])) index++
  return index
}

/** `function name(` and `name = <function>` — where a named function is declared; functionAt reads what follows. */
const FUNCTION_DECLARATION = /(?<![\w$])function(?:\s*\*)?\s*([A-Za-z_$][\w$]*)\s*\(|(?<![\w$])([A-Za-z_$][\w$]*)\s*=\s*(?=async\b|function\b|\(|[A-Za-z_$][\w$]*\s*=>)/g

type NamedFunction = {
  name: string
  declaredAt: number
  /** Where the name itself is written in the declaration — not a reference to the function. */
  nameAt: number
  body: [number, number]
}

/** Every named function on the page, first declaration of a name winning as in a lookup by name. */
function namedFunctions(code: Code): Map<string, NamedFunction> {
  const html = code.text
  const functions = new Map<string, NamedFunction>()
  for (const match of matchAll(html, FUNCTION_DECLARATION)) {
    const name = match[1] ?? match[2]
    if (functions.has(name)) continue
    const body = functionAt(code, match[1] ? match.index : match.index + match[0].length)
    const nameAt = match[1] ? match.index + match[0].indexOf(name, 'function'.length) : match.index
    if (body) functions.set(name, { name, declaredAt: match.index, nameAt, body })
  }

  return functions
}

/**
 * Index where the expression starting at `start` ends: the first `,`, `;` or newline at its own depth, or the
 * bracket that closes around it. Brackets and literals are jumped over through the lexer's tables, and the walk
 * gives up after MAX_EXPRESSION_STEPS, so a run of unterminated handlers cannot make each one scan to the end.
 */
function endOfExpression(code: Code, start: number): number {
  const html = code.text
  const { literalEnd } = code.lex
  const limit = codeLimit(code, start)
  for (let index = start, steps = 0; index < limit; index++, steps++) {
    if (steps > MAX_EXPRESSION_STEPS) return index
    if (literalEnd[index] > 0) {
      index = literalEnd[index] - 1
      continue
    }
    const char = html[index]
    if (char === '(' || char === '{' || char === '[') {
      const after = balancedEnd(code, index)
      if (after < 0) return limit
      index = after - 1
      continue
    }
    if (char === ')' || char === ']' || char === '}' || char === ',' || char === ';' || char === '\n') return index
  }
  return limit
}

/** A handler expression longer than this, outside brackets, is cut here. Real ones are a call or two. */
const MAX_EXPRESSION_STEPS = 2000

/** Backend calls inside the argument list of each match, e.g. the callback passed to setInterval. */
function callsInsideCalls(code: Code, opener: RegExp): BackendHit[] {
  const html = code.text
  return matchAll(html, opener).flatMap((match) => {
    const start = html.indexOf('(', match.index)
    return backendCallsIn(code, start)
  })
}

/** Backend calls inside the body that follows each match, e.g. a loop body — a block, or a single statement without braces. */
function callsInsideBlocks(code: Code, opener: RegExp): BackendHit[] {
  const html = code.text
  return matchAll(html, opener).flatMap((match) => {
    const parenStart = html.indexOf('(', match.index)
    const parenEnd = balancedEnd(code, parenStart)
    if (parenEnd < 0) return []
    const bodyStart = parenEnd + html.slice(parenEnd).search(/\S/)
    if (html[bodyStart] === '{') return backendCallsIn(code, bodyStart)

    const statementEnd = html.slice(bodyStart).search(/[;\n]/)
    const region = html.slice(bodyStart, statementEnd < 0 ? html.length : bodyStart + statementEnd)
    return matchAll(region, BACKEND_CALL_PATTERN).map((call) => ({ member: call[1], index: bodyStart + call.index }))
  })
}

function backendCallsIn(code: Code, start: number): BackendHit[] {
  const html = code.text
  if (start < 0) return []
  const end = balancedEnd(code, start)
  if (end < 0) return []
  const region = html.slice(start, end)
  return matchAll(region, BACKEND_CALL_PATTERN).map((match) => ({
    member: match[1],
    index: start + match.index,
  }))
}

/**
 * Index just past the partner of the `(`, `{` or `[` at `start`, or -1 when it has none. A lookup in the
 * page's bracket table (code-lexer.ts), which already skipped strings, templates, regexes and comments,
 * and never paired across the end of a script.
 */
function balancedEnd(code: Code, start: number): number {
  if (!'({['.includes(code.text[start] ?? '')) return -1
  const after = code.lex.closeAfter[start]
  return after > 0 ? after : -1
}

/**
 * setInterval calls that fire faster than FAST_TIMER_MS and whose own callback reaches the backend — written
 * inline, or a named function, followed through the named functions it calls. A missing delay is 0, the
 * fastest there is; a computed one is not read.
 */
function fastIntervals(page: Page): { delay: number; index: number; members: string[] }[] {
  const text = page.text
  return matchAll(text, /\bsetInterval\s*\(/g).flatMap((match) => {
    const open = text.indexOf('(', match.index)
    const [callback, delaySpan] = topLevelArguments(page, open)
    const delayText = delaySpan && text.slice(delaySpan[0], delaySpan[1])
    const delay = delayText === undefined || delayText.trim() === '' ? 0 : /^\d+$/.test(delayText.trim()) ? Number(delayText) : NaN
    if (!(delay < FAST_TIMER_MS)) return []

    const callbackStart = skipSpace(text, callback[0])
    const region =
      functionAt(page, callbackStart) ?? page.functions.get(text.slice(callbackStart, callback[1]).trim())?.body ?? null
    const members = region ? backendReachedFrom(page, region) : []
    return members.length ? [{ delay, index: match.index, members }] : []
  })
}

/** The argument spans of the call whose `(` is at `open`, split at its own commas; the last one may be empty. */
function topLevelArguments(page: Page, open: number): [number, number][] {
  const { text, lex } = page
  const close = (balancedEnd(page, open) > 0 ? balancedEnd(page, open) : codeLimit(page, open)) - 1
  const spans: [number, number][] = []
  let from = open + 1
  for (let index = from; index < close; index++) {
    if (lex.literalEnd[index] > 0) index = lex.literalEnd[index] - 1
    else if (balancedEnd(page, index) > 0) index = balancedEnd(page, index) - 1
    else if (text[index] === ',') {
      spans.push([from, index])
      from = index + 1
    }
  }
  spans.push([from, close])
  return spans
}

/** Backend members called in `region`, or in any named function called from it, to any depth. */
function backendReachedFrom(page: Page, region: [number, number]): string[] {
  const members = new Set<string>()
  const seen = new Set<string>()
  const pending = [region]
  while (pending.length) {
    const [from, to] = pending.pop() as [number, number]
    const slice = page.text.slice(from, to)
    for (const call of matchAll(slice, BACKEND_CALL_PATTERN)) members.add(call[1])
    for (const call of matchAll(slice, /(?<![\w$.])([A-Za-z_$][\w$]*)\s*\(/g)) {
      const body = page.functions.get(call[1])?.body
      if (body && !seen.has(call[1])) {
        seen.add(call[1])
        pending.push(body)
      }
    }
  }
  return [...members]
}

// --- configuration ---------------------------------------------------------

function configurationFacts(nodes: Record<string, unknown>[], html: string): ConfigurationFacts {
  const node = nodes[0]

  return {
    hasQuery: Boolean(asString(node.query)?.trim()),
    query: asString(node.query),
    allowedRuleTags: allowedRuleTagsFrom(asString(node.allowedRuleTags)),
    targetGraph: asString(node.targetGraph),
    minHeight: asString(node.minHeight),
    mutationLogs: {
      enabled: node.mutationLogsEnabled === true,
      graph: asString(node.mutationLogsGraph),
      customStatements: asString(node.mutationLogsCustomStatements),
    },
    declaredConfigFields: parseHtmlConfigFields(html).map((field) => ({
      name: field.name,
      type: field.type,
      label: field.label ?? field.name,
      values: nodes.map((placement) => {
        const stored = placement[CONFIG_FIELD_PREFIX + field.name]
        return stored == null ? null : String(stored)
      }),
    })),
  }
}

// --- bridge spelling ---------------------------------------------------------

/**
 * Rewrite the other everyday spellings of a bridge call to `wistor.member(`, the one every pattern here reads:
 * `wistor["x"]`, `wistor?.["x"]`, `window["wistor"]`, `wistor?.x` and `wistor.x?.(`. Padded with spaces so every
 * offset stays put, the same promise blankComments keeps. Computed keys are left as they are: bridge-indirect
 * reports them.
 */
function canonicalBridge(source: string): string {
  const padded = (text: string, length: number) => text.padEnd(length, ' ')
  return source
    .replace(/\bwindow[ \t]*\[[ \t]*(['"`])wistor\1[ \t]*\]/g, (match) => padded('window.wistor', match.length))
    .replace(/\bwistor[ \t]*(?:\?\.[ \t]*)?\[[ \t]*(['"`])([A-Za-z_$][\w$]*)\1[ \t]*\]/g, (match, _quote, member: string) =>
      padded(`wistor.${member}`, match.length)
    )
    .replace(/\bwistor([ \t]*)\?\.(?![ \t]*\[)/g, 'wistor$1 .')
    .replace(/(\bwistor[ \t]*\.[ \t]*[A-Za-z_$][\w$]*[ \t]*)\?\.(?=[ \t]*\()/g, '$1  ')
}

// --- comments --------------------------------------------------------------

/**
 * Replace comment bodies with spaces so nothing inside them is scanned, while
 * every other character keeps its position. Offsets surviving is the point: the
 * patterns run over this, the evidence snippets and line numbers come from the
 * original, and the two only agree if the two strings are the same length.
 *
 * The comments come from code-lexer.ts, layered like the browser: html-structure
 * decides where HTML comments, scripts and styles are; JavaScript comment rules
 * then apply inside scripts and handler attributes, CSS rules (block comments
 * only) inside styles — never past the end the HTML layer set. The lexer is
 * string-, template- and regex-literal-aware: blanking from the first `//` would
 * eat the host out of every `https://` URL, and the escaping helper `/[&<>"']/g`
 * holds a quote that, read as a string opener, would invert strings and code.
 */
export function blankComments(source: string): string {
  return blank(source, lexPage(source).comments)
}

function blank(source: string, comments: [number, number][]): string {
  const out = source.split('')
  for (const [from, to] of comments) {
    for (let index = from; index < Math.min(to, out.length); index++) {
      if (out[index] !== '\n') out[index] = ' '
    }
  }
  return out.join('')
}

/**
 * Where code starting at `index` must stop: the end of the script or handler attribute holding it, or the
 * end of the source for code outside any. Decided by the HTML layer, so no JavaScript walker decides it.
 */
function codeLimit(code: Code, index: number): number {
  return codeRangeAt(code.lex, index)?.end ?? code.text.length
}

// --- helpers ---------------------------------------------------------------

type Locator = (index: number) => Evidence

const MAX_SNIPPET = 200

function makeLocator(source: string): Locator {
  const lineStarts = [0]
  for (let index = 0; index < source.length; index++) {
    if (source[index] === '\n') lineStarts.push(index + 1)
  }
  /* One entry per line: a page written on one long line puts every hit on it, and re-reading that line per hit was quadratic. */
  const byLine = new Map<number, Evidence>()

  return (index) => {
    let low = 0
    let high = lineStarts.length - 1
    while (low < high) {
      const mid = Math.ceil((low + high) / 2)
      if (lineStarts[mid] <= index) low = mid
      else high = mid - 1
    }
    const known = byLine.get(low)
    if (known) return known

    const end = low + 1 < lineStarts.length ? lineStarts[low + 1] - 1 : source.length
    const line = source.slice(lineStarts[low], end).trim()
    const evidence = {
      line: low + 1,
      snippet: line.length > MAX_SNIPPET ? `${line.slice(0, MAX_SNIPPET)}…` : line,
    }
    byLine.set(low, evidence)
    return evidence
  }
}

function matchAll(source: string, pattern: RegExp): RegExpExecArray[] {
  /* Fresh RegExp per call: the rule tables are module-level, and a shared lastIndex would make results depend on scan order. */
  const scanner = new RegExp(pattern.source, pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`)
  const matches: RegExpExecArray[] = []
  let match = scanner.exec(source)
  while (match) {
    matches.push(match)
    if (match.index === scanner.lastIndex) scanner.lastIndex++
    match = scanner.exec(source)
  }
  return matches
}

function dedupeEvidence(evidence: Evidence[]): Evidence[] {
  const seen = new Set<number>()
  return evidence
    .filter((item) => (seen.has(item.line) ? false : (seen.add(item.line), true)))
    .sort((a, b) => a.line - b.line)
}

function highestSeverity(widgets: CodeWidgetAnalysis[]): Severity | null {
  const all = widgets.flatMap((widget) => widget.findings.map((finding) => finding.severity))
  if (!all.length) return null
  return all.reduce((worst, severity) => (SEVERITY_RANK[severity] < SEVERITY_RANK[worst] ? severity : worst))
}

function bySeverityThenId(a: Finding, b: Finding): number {
  return SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || a.id.localeCompare(b.id)
}

function unique<T>(values: T[]): T[] {
  return Array.from(new Set(values))
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}
